import React, { useState, useEffect, useRef, useMemo } from 'react';
import { PitchZoneGrid } from './PlayerOddsTable';
import BullpenDropdown from './BullpenDropdown';
import { getTeamLogoUrl, getBookLogoUrl } from '../utils/formatters';
import { buildBullpenList } from '../utils/mlbBullpen';
import { useDarkMode } from '../contexts/DarkModeContext';

// Continuous colour stops (rate → RGBA). One set per whiff denominator: on
// swings the MLB average is ~24-25%, on pitches (SwStr%) ~11%, so the ramps
// straddle different midpoints. Rates below the coldest stop clamp to deep
// blue; rates above the hottest stop clamp to deep red.
const WHIFF_STOPS_BY_SWINGS = [
  { r: 0.10, rgba: [13, 110, 253, 0.65 * 255] },
  { r: 0.18, rgba: [13, 110, 253, 0.40 * 255] },
  { r: 0.25, rgba: [108, 117, 125, 0.45 * 255] },
  { r: 0.32, rgba: [245, 158, 11, 0.55 * 255] },
  { r: 0.40, rgba: [220, 53, 69, 0.55 * 255] },
  { r: 0.50, rgba: [220, 53, 69, 0.85 * 255] },
];
const WHIFF_STOPS_BY_PITCHES = [
  { r: 0.04, rgba: [13, 110, 253, 0.65 * 255] },
  { r: 0.08, rgba: [13, 110, 253, 0.40 * 255] },
  { r: 0.11, rgba: [108, 117, 125, 0.45 * 255] },
  { r: 0.15, rgba: [245, 158, 11, 0.55 * 255] },
  { r: 0.20, rgba: [220, 53, 69, 0.55 * 255] },
  { r: 0.26, rgba: [220, 53, 69, 0.85 * 255] },
];
const lerp = (a, b, t) => a + (b - a) * t;
const rateToRgbaWith = (rate, stops) => {
  if (rate == null || Number.isNaN(rate)) return [0, 0, 0, 0];
  if (rate <= stops[0].r) return stops[0].rgba;
  if (rate >= stops[stops.length - 1].r) return stops[stops.length - 1].rgba;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (rate >= a.r && rate <= b.r) {
      const t = (rate - a.r) / (b.r - a.r);
      return [
        lerp(a.rgba[0], b.rgba[0], t),
        lerp(a.rgba[1], b.rgba[1], t),
        lerp(a.rgba[2], b.rgba[2], t),
        lerp(a.rgba[3], b.rgba[3], t),
      ];
    }
  }
  return [0, 0, 0, 0];
};
// Statcast zone centres in the 100x100 heat-map viewBox. Zones 1-9 are the
// 3x3 strike-zone grid; 11-14 are the chase quadrants (anchored at the
// outer corner where the label lives, since the region is L-shaped).
const ZONE_CENTERS = {
  '1': [27.333, 27.333], '2': [50, 27.333], '3': [72.667, 27.333],
  '4': [27.333, 50],     '5': [50, 50],     '6': [72.667, 50],
  '7': [27.333, 72.667], '8': [50, 72.667], '9': [72.667, 72.667],
  '11': [10, 10],  '12': [90, 10],
  '13': [10, 90],  '14': [90, 90],
};
// Inverse-distance-weighted interpolation over the 13 zone centres. Power 2
// gives a smooth blend without letting a single hot zone bleed too far.
const idwInterpolate = (x, y, points) => {
  let num = 0, den = 0;
  for (let i = 0; i < points.length; i++) {
    const [px, py, val] = points[i];
    const dx = x - px, dy = y - py;
    const d2 = dx * dx + dy * dy;
    if (d2 < 0.5) return val;
    const w = 1 / (d2 * d2);
    num += val * w;
    den += w;
  }
  return den > 0 ? num / den : null;
};

// Whiff-rate zone heat map for a single batter/pitcher. Two render modes:
//   'smooth'   — IDW-interpolated continuous heat map generated to an off-
//                screen canvas and embedded as an SVG <image>. Zone dividers
//                and a home plate overlay for spatial context.
//   'sections' — Discrete per-zone rectangles (the original look), coloured
//                against the same absolute MLB whiff-rate scale.
// `whiffDenom` controls the rate definition: 'swings' (whiffs/swings) or
// 'pitches' (whiffs/pitches, aka SwStr%). The colour ramp is swapped to
// match so the palette stays informative under each definition.
const WhiffZoneHeatmap = ({ byZone, size = 160, mode = 'smooth', whiffDenom = 'swings' }) => {
  const stops = whiffDenom === 'pitches' ? WHIFF_STOPS_BY_PITCHES : WHIFF_STOPS_BY_SWINGS;
  const rateOf = (swings, whiffs, pitches) => {
    if (whiffDenom === 'pitches') return pitches > 0 ? whiffs / pitches : null;
    return swings > 0 ? whiffs / swings : null;
  };
  const zones = {};
  Object.entries(byZone || {}).forEach(([key, s]) => {
    const swings = s.swings || 0;
    const whiffs = s.whiffs || 0;
    const pitches = s.seen || s.thrown || 0;
    zones[key] = {
      pitches,
      swings,
      whiffs,
      rate: rateOf(swings, whiffs, pitches),
    };
  });
  // Denominator-aware presence check: "swings" needs a swing count somewhere;
  // "pitches" only needs at least one pitch to render.
  const totalDenom = Object.values(zones).reduce(
    (sum, z) => sum + (whiffDenom === 'pitches' ? z.pitches : z.swings),
    0
  );
  // Smooth mode: generate an interpolated pixel-level heat map once per
  // byZone input. We render to an offscreen canvas at 100x100 (matching the
  // viewBox) then embed as a data URL <image>. useMemo must run every render
  // (React rules-of-hooks) so it sits above the early-return below and
  // internally guards on `mode`/`totalDenom`.
  const smoothDataURL = useMemo(() => {
    if (mode !== 'smooth') return null;
    if (totalDenom === 0) return null;
    if (typeof document === 'undefined') return null;
    const points = [];
    Object.entries(ZONE_CENTERS).forEach(([key, [px, py]]) => {
      const rate = zones[key]?.rate;
      if (rate != null) points.push([px, py, rate]);
    });
    if (points.length < 3) return null;
    const canvas = document.createElement('canvas');
    const W = 100, H = 100;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const img = ctx.createImageData(W, H);
    for (let py = 0; py < H; py++) {
      for (let px = 0; px < W; px++) {
        const rate = idwInterpolate(px + 0.5, py + 0.5, points);
        const [r, g, b, a] = rateToRgbaWith(rate, stops);
        const i = (py * W + px) * 4;
        img.data[i] = r;
        img.data[i + 1] = g;
        img.data[i + 2] = b;
        img.data[i + 3] = a;
      }
    }
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL();
    // Recompute only when byZone / mode / denominator change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byZone, mode, whiffDenom]);
  if (totalDenom === 0) return null;
  // Absolute diverging scale sourced from the ramp for the active denom.
  // Rates are converted to RGBA once and rendered as `rgba(...)` strings so
  // the sectioned and smooth modes stay perfectly in sync.
  const fillFor = (z) => {
    if (!z || z.rate == null) return 'rgba(255,255,255,0.04)';
    const [r, g, b, a] = rateToRgbaWith(z.rate, stops);
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${(a / 255).toFixed(3)})`;
  };
  const rateText = (z) => (z && z.rate != null ? `${Math.round(z.rate * 100)}%` : '');
  const secondaryText = (z) => {
    if (!z) return '';
    if (whiffDenom === 'pitches') return z.pitches > 0 ? `${z.pitches} p` : '';
    return z.swings > 0 ? `${z.swings} sw` : '';
  };
  const zoneTip = (zoneKey, z) => {
    if (!z || (z.swings === 0 && z.pitches === 0)) return `Zone ${zoneKey} · No pitches`;
    const label = whiffDenom === 'pitches' ? 'Whf / Pch' : 'Whf / Swg';
    const rate = z.rate != null ? `${Math.round(z.rate * 100)}%` : '-';
    return `Zone ${zoneKey}\nPitches: ${z.pitches}\nSwings: ${z.swings}\nWhiffs: ${z.whiffs}\n${label}: ${rate}`;
  };
  const quadW = 50;
  const corners = [
    { zone: '11', x: 0, y: 0, labelX: 10, labelY: 10 },
    { zone: '12', x: quadW, y: 0, labelX: 90, labelY: 10 },
    { zone: '13', x: 0, y: quadW, labelX: 10, labelY: 90 },
    { zone: '14', x: quadW, y: quadW, labelX: 90, labelY: 90 },
  ];
  const szMin = 16, szMax = 84, sz = szMax - szMin, cell = sz / 3;
  const szCells = [
    { zone: '1', row: 0, col: 0 }, { zone: '2', row: 0, col: 1 }, { zone: '3', row: 0, col: 2 },
    { zone: '4', row: 1, col: 0 }, { zone: '5', row: 1, col: 1 }, { zone: '6', row: 1, col: 2 },
    { zone: '7', row: 2, col: 0 }, { zone: '8', row: 2, col: 1 }, { zone: '9', row: 2, col: 2 },
  ];
  const isSmooth = mode === 'smooth';
  // ViewBox reserves space below the SZ (100..128) for the home plate.
  const svgW = size;
  const svgH = size * 1.28;
  return (
    <svg width={svgW} height={svgH} viewBox="0 0 100 128" style={{ display: 'block' }}>
      {/* Home plate — pentagon under the SZ, wide edge at top, point down */}
      <path
        d="M 22 102 L 78 102 L 78 110 L 50 120 L 22 110 Z"
        fill="rgba(255,255,255,0.08)"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="0.6"
      />
      {isSmooth && smoothDataURL && (
        // The interpolated bitmap covers the whole 100x100 area (SZ + chase).
        // preserveAspectRatio="none" so the 100x100 canvas maps 1:1 to the
        // viewBox; imageRendering pixelated keeps colours sharp at high DPI.
        <image
          href={smoothDataURL}
          x="0"
          y="0"
          width="100"
          height="100"
          preserveAspectRatio="none"
          style={{ imageRendering: 'auto' }}
        />
      )}
      {corners.map(({ zone, x, y, labelX, labelY }) => {
        const z = zones[zone];
        return (
          <g key={zone}>
            <title>{zoneTip(zone, z)}</title>
            {!isSmooth && (
              <rect x={x} y={y} width={quadW} height={quadW} fill={fillFor(z)} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
            )}
            {isSmooth && (
              // Transparent hit-target so the browser tooltip still fires.
              <rect x={x} y={y} width={quadW} height={quadW} fill="transparent" />
            )}
            {z && (whiffDenom === 'pitches' ? z.pitches > 0 : z.swings > 0) && (
              <>
                <text x={labelX} y={labelY - 1} textAnchor="middle" fill="white" fontSize="5" fontWeight="700" style={{ pointerEvents: 'none' }}>{rateText(z)}</text>
                <text x={labelX} y={labelY + 5} textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="3.5" style={{ pointerEvents: 'none' }}>{secondaryText(z)}</text>
              </>
            )}
          </g>
        );
      })}
      <rect x={szMin} y={szMin} width={sz} height={sz} fill={isSmooth ? 'none' : 'rgba(0,0,0,0.4)'} stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
      {szCells.map(({ zone, row, col }) => {
        const z = zones[zone];
        const rx = szMin + col * cell;
        const ry = szMin + row * cell;
        const cx = rx + cell / 2;
        const cy = ry + cell / 2;
        return (
          <g key={zone}>
            <title>{zoneTip(zone, z)}</title>
            {!isSmooth && (
              <rect x={rx} y={ry} width={cell} height={cell} fill={fillFor(z)} stroke="rgba(255,255,255,0.22)" strokeWidth="0.3" />
            )}
            {isSmooth && (
              <rect x={rx} y={ry} width={cell} height={cell} fill="transparent" stroke="rgba(255,255,255,0.18)" strokeWidth="0.2" />
            )}
            {z && (whiffDenom === 'pitches' ? z.pitches > 0 : z.swings > 0) && (
              <>
                <text x={cx} y={cy - 1} textAnchor="middle" fill="white" fontSize="6" fontWeight="700" style={{ pointerEvents: 'none' }}>{rateText(z)}</text>
                <text x={cx} y={cy + 5} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="4" style={{ pointerEvents: 'none' }}>{secondaryText(z)}</text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// Book logo with text fallback when the image fails to load (e.g. "multi")
const BookLogo = ({ book }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [book]);
  if (!book) return '-';
  if (errored) return book;
  return (
    <img
      src={getBookLogoUrl(book)}
      alt={book}
      style={{ height: '16px', objectFit: 'contain' }}
      onError={() => setErrored(true)}
    />
  );
};

const MatchupsPanel = ({
  visibleGameId: visibleGameIdProp,
  visibleGame: visibleGameProp,  // the game object from filteredGamesForRender
  games = [],                     // all games for dropdown
  playersByGameIdRef,             // ref to odds data
  isMobile,
  showBetslipPanel,
  marketBannerBottom,
  onClose,
  onPlayerClick,
  onPlayerContextMenu,
}) => {
  // Override state: when user picks from dropdown, use this instead of scroll-driven visibleGameId
  const [overrideGameId, setOverrideGameId] = useState(null);
  const visibleGameId = overrideGameId || visibleGameIdProp;
  const visibleGame = overrideGameId ? games.find(g => g.gameIDmlb === overrideGameId) : visibleGameProp;

  // Clear override when the panel closes (handled at parent via unmount)
  // Reset override if prop changes to match (user scrolled to that game)
  useEffect(() => {
    if (overrideGameId && overrideGameId === visibleGameIdProp) {
      setOverrideGameId(null);
    }
  }, [visibleGameIdProp, overrideGameId]);
  const { isDarkMode } = useDarkMode();
  const getTeamLogoUrlForSport = (teamAbbrev) => getTeamLogoUrl(teamAbbrev, isDarkMode, 'mlb');

  // Look up player hand info from game data (awayPlayers/homePlayers)
  // Right-click handler for batter rows — opens the App-level context menu
  // (Add to Scratchpad, etc.). Strips the mlb_ prefix that lives on raw API
  // IDs and uses `mlb_${id}` to match the format the App-level handlers expect.
  const handleBatterContextMenu = (batterId, batterName, teamAbv) => (e) => {
    if (!onPlayerContextMenu || !batterId) {
      // Suppress the native browser menu either way so we don't show a bare
      // "Reload / Save Image" menu when the handler short-circuits.
      e.preventDefault();
      return;
    }
    const cleanId = String(batterId).replace(/^mlb_/, '');
    onPlayerContextMenu(e, visibleGameId, `mlb_${cleanId}`, batterName, teamAbv || null);
  };

  const getPlayerHandFromGame = (playerId) => {
    const id = String(playerId).replace(/^mlb_/, '');
    const key = `ID${id}`;
    const player = visibleGame?.awayPlayers?.[key] || visibleGame?.homePlayers?.[key];
    if (!player) return null;
    return {
      bats: player.batSide?.code || null,
      throws: player.pitchHand?.code || null
    };
  };

  // Render player panel icon
  const renderPlayerIcon = (batterId, batterName, teamAbv) => {
    if (!onPlayerClick) return null;
    return (
      <span
        style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '0.6rem', color: '#6c757d', verticalAlign: 'middle' }}
        onClick={(e) => {
          e.stopPropagation();
          const awayTeam = matchupsData?.away?.team;
          const homeTeam = matchupsData?.home?.team;
          onPlayerClick(visibleGameId, batterId, batterName, null, awayTeam, homeTeam, teamAbv, null);
        }}
        title="Open player panel"
      >&#x25B6;</span>
    );
  };

  // Play button for ball flight viewer
  const renderPlayButton = (ab) => {
    const gamePk = ab.gamePk || ab.game_pk;
    const atBatIndex = ab.atBatIndex ?? ab.at_bat_index;
    if (!gamePk || atBatIndex == null) return null;
    const hasHitData = ab.playEvents?.some(pe => pe.hitData);
    if (!hasHitData) return null;
    const season = ab.officialDate ? ab.officialDate.slice(0, 4) : String(new Date().getFullYear());
    return (
      <span
        style={{ cursor: 'pointer', color: '#6c757d', fontSize: '0.7rem', marginLeft: '4px', verticalAlign: 'middle' }}
        onClick={(e) => { e.stopPropagation(); setBallFlightPlay({ playId: `${gamePk}_${atBatIndex}`, season }); }}
        title="Ball flight"
      >&#x25B6;</span>
    );
  };

  // Render batting order number badge (or nothing if not in lineup)
  const renderBatOrder = (batterId, teamIdx) => {
    const pos = getBattingOrder(batterId, teamIdx);
    if (pos === Infinity) return null;
    return <span style={{ color: '#6c757d', fontSize: '0.6rem', marginRight: '4px' }}>{pos + 1}</span>;
  };

  // Get batting order position from game lineup arrays (1-9, or Infinity if not in lineup)
  const getBattingOrder = (batterId, teamIdx) => {
    const lineup = teamIdx === 0
      ? visibleGame?.awayStartingLineup
      : visibleGame?.homeStartingLineup;
    if (!lineup || lineup.length === 0) return Infinity;
    const numericId = parseInt(String(batterId).replace(/^mlb_/, ''));
    const idx = lineup.findIndex(id => parseInt(id) === numericId);
    return (idx >= 0 && idx < 9) ? idx : Infinity;
  };

  // All 19 state declarations
  const [matchupsData, setMatchupsData] = useState(null);
  const [matchupsDataGameId, setMatchupsDataGameId] = useState(null);
  // BvP bullpen override: swap the pitcher shown in the BvP view for a
  // reliever picked from the bullpen dropdown, keyed by team abbreviation.
  // The corresponding stats map is filled in on selection via
  // /api/stats/mlb/atbats/matchup/{batter}/{reliever}.
  const [bvpOverridePitchers, setBvpOverridePitchers] = useState({}); // { teamAbv: {id, name, hand} }
  const [bvpOverrideStats, setBvpOverrideStats] = useState({});       // { teamAbv: { batterId: statsObj } }
  // Full pitcher doc for each override (seasonStats.byPitch etc.), so the
  // All view's Pitches columns can recalculate against the reliever instead
  // of the probable pitcher's arsenal.
  const [bvpOverridePitcherData, setBvpOverridePitcherData] = useState({}); // { teamAbv: pitcherDoc }
  const [matchupsLoading, setMatchupsLoading] = useState(false);
  const [matchupsView, setMatchupsView] = useState('bvp');
  const [dingerData, setDingerData] = useState(null);
  const [dingerLoading, setDingerLoading] = useState(false);
  const [dingerMode, setDingerMode] = useState('top'); // 'top' | 'matchup' | 'bvp'
  const [pitchesData, setPitchesData] = useState(null);
  const [pitchesLoading, setPitchesLoading] = useState(false);
  const [ballFlightPlay, setBallFlightPlay] = useState(null); // { playId, season }
  const [pitchIdx0, setPitchIdx0] = useState(0);
  const [pitchIdx1, setPitchIdx1] = useState(0);
  const [splitPitchIdx, setSplitPitchIdx] = useState({});
  const [pitchesSubMode, setPitchesSubMode] = useState('summary'); // 'summary' | 'all' (detail)
  const [pitchesSplitMode, setPitchesSplitMode] = useState('splits'); // 'all' | 'splits'
  const [pitchesMetricMode, setPitchesMetricMode] = useState('traditional');
  const [pitchesYear, setPitchesYear] = useState('2026');
  const [lastPeriod, setLastPeriod] = useState('last7');
  const [lastSubMode, setLastSubMode] = useState('summary'); // 'summary' | 'detail'
  const [lastSplit, setLastSplit] = useState('overall'); // 'overall' | 'vsLHP' | 'vsRHP'
  const [bvpYear, setBvpYear] = useState('all');
  // K-view render mode — 'smooth' interpolates the 13 zone rates into a
  // continuous heat map; 'sections' shows discrete per-zone fills.
  const [kHeatmapMode, setKHeatmapMode] = useState('smooth');
  // K-view layout — 'individual' shows one card per batter split by
  // handedness; 'composite' aggregates all batters into a single team-level
  // heat map (paired with the opposing pitcher's overall map).
  const [kLayoutMode, setKLayoutMode] = useState('individual');
  // K-view whiff-rate denominator — 'swings' = whiffs/swings (miss rate
  // when the batter offers); 'pitches' = whiffs/pitches (aka SwStr%).
  const [kWhiffDenom, setKWhiffDenom] = useState('swings');
  // All-view per-section Splits toggles — when on, the column is filtered to
  // the opposing pitcher's hand (Pitches uses byPitchHand; Last switches the
  // window's split key to vsLHP / vsRHP).
  const [allSplitsPitches, setAllSplitsPitches] = useState(true);
  const [allSplitsLast, setAllSplitsLast] = useState(true);
  const [matchupAtBatBatter, setMatchupAtBatBatter] = useState(null);
  const [matchupAtBatData, setMatchupAtBatData] = useState(null);
  const [matchupAtBatLoading, setMatchupAtBatLoading] = useState(false);
  const [matchupAtBatExpandedIdx, setMatchupAtBatExpandedIdx] = useState(null);
  const [matchupAtBatViewMode, setMatchupAtBatViewMode] = useState('table');

  const pitchesFetchedGameRef = useRef(null);

  // Effect: fetch matchups data
  useEffect(() => {
    if (!visibleGameId) {
      return;
    }

    // Clear pitches cache when game changes so it re-fetches
    pitchesFetchedGameRef.current = null;
    setPitchesData(null);
    // Clear at-bat detail when game changes
    setMatchupAtBatBatter(null);
    setMatchupAtBatData(null);

    if (visibleGameId.startsWith('locked_')) {
      setMatchupsData(null);
      setMatchupsDataGameId(null);
      setBvpOverridePitchers({});
      setBvpOverrideStats({});
      setBvpOverridePitcherData({});
      return;
    }

    let cancelled = false;
    const fetchMatchupsData = async () => {
      // Only show loading spinner if we have no data yet (first load)
      if (!matchupsData) setMatchupsLoading(true);
      try {
        const response = await fetch(`/api/stats/mlb/matchups/${visibleGameId}`);
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) {
            setMatchupsData(data);
            setMatchupsDataGameId(visibleGameId);
          }
        } else {
          console.error('Matchups fetch failed:', response.status);
        }
      } catch (error) {
        if (!cancelled) console.error('Matchups fetch error:', error);
      } finally {
        if (!cancelled) setMatchupsLoading(false);
      }
    };

    fetchMatchupsData();
    return () => { cancelled = true; };
  }, [visibleGameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For each active bullpen-override selection, fetch the batter-vs-reliever
  // matchup stats for the opposing lineup. Result is a per-team map of
  // { batterId: stats } so getBvpStats can swap in fresh numbers when the
  // reliever is displayed. Any prior override entry for a team is cleared
  // before the new fetch fires so we don't show stale reliever numbers.
  useEffect(() => {
    if (!matchupsData) return;
    const entries = Object.entries(bvpOverridePitchers).filter(([, p]) => p);
    if (entries.length === 0) return;
    let cancelled = false;
    (async () => {
      const stripPrefix = (id) => String(id).replace(/^mlb_/, '');
      for (const [teamAbv, pitcher] of entries) {
        // Batter set is the union of everything the BvP render could show:
        //   1. matchupsData[side].matchups — populated when the probable
        //      pitcher exists (historical matchup rows already fetched).
        //   2. visibleGame.{away,home}StartingLineup — the opposing lineup
        //      IDs, needed when there's no probable pitcher OR for lineup
        //      extras that don't have prior history against the probable.
        //   3. odds players (playersByGameIdRef) — the same "lineup extras"
        //      source the BvP render uses, so we don't leave rows blank
        //      when neither #1 nor #2 has them.
        // Keys stored below are all stripped-of-mlb_ so getBvpStats can
        // strip m.batterId at lookup time and hit consistently.
        const teamSide = matchupsData.home?.team === teamAbv
          ? matchupsData.home
          : matchupsData.away?.team === teamAbv
            ? matchupsData.away
            : null;
        const isHomeSide = matchupsData.home?.team === teamAbv;
        const battingTeamAbv = isHomeSide ? matchupsData.away?.team : matchupsData.home?.team;
        const matchupBatterIds = (teamSide?.matchups || []).map(m => stripPrefix(m.batterId));
        const lineupIds = (isHomeSide
          ? visibleGame?.awayStartingLineup
          : visibleGame?.homeStartingLineup) || [];
        const oddsPlayers = playersByGameIdRef.current?.[visibleGameId]?.players || [];
        const oddsBatterIds = oddsPlayers
          .filter(p => {
            const pid = String(p.playerId || '');
            if (!pid || pid.startsWith('team_') || pid.startsWith('totals_') || pid.startsWith('spreads_')) return false;
            if (p.position === 'pitcher' || p.position === 'P') return false;
            const t = p.team || p.teamAbv;
            return battingTeamAbv ? (t === battingTeamAbv) : true;
          })
          .map(p => stripPrefix(p.playerId));
        const idSet = new Set([
          ...matchupBatterIds,
          ...lineupIds.map(id => String(id)),
          ...oddsBatterIds,
        ]);
        const batters = Array.from(idSet).map(id => ({ batterId: id }));
        if (batters.length === 0) continue;
        try {
          const results = await Promise.all(batters.map(async b => {
            const cleanBatter = b.batterId; // already stripped
            const cleanPitcher = stripPrefix(pitcher.id);
            try {
              const res = await fetch(`/api/stats/mlb/atbats/matchup/${cleanBatter}/${cleanPitcher}`);
              if (!res.ok) return [cleanBatter, null];
              const data = await res.json();
              return [cleanBatter, data?.stats || null];
            } catch { return [cleanBatter, null]; }
          }));
          if (cancelled) return;
          setBvpOverrideStats(prev => ({
            ...prev,
            [teamAbv]: Object.fromEntries(results),
          }));
        } catch { /* swallow */ }
      }
    })();
    return () => { cancelled = true; };
  }, [bvpOverridePitchers, matchupsData]);

  // Fetch the reliever's full pitcher doc for each active override so the
  // Pitches columns in the All view can recalc against the reliever's
  // arsenal (byPitch, byPitchHand). Uses the same season as the current
  // pitches view so splits align with the surrounding data.
  useEffect(() => {
    const entries = Object.entries(bvpOverridePitchers).filter(([, p]) => p);
    if (entries.length === 0) return;
    let cancelled = false;
    (async () => {
      const yearParam = pitchesYear ? `?season=${pitchesYear}` : '';
      for (const [teamAbv, pitcher] of entries) {
        try {
          const cleanId = String(pitcher.id).replace(/^mlb_/, '');
          const res = await fetch(`/api/stats/mlb/atbats/pitcher/${cleanId}${yearParam}`);
          if (!res.ok || cancelled) continue;
          const doc = await res.json();
          if (cancelled) return;
          setBvpOverridePitcherData(prev => ({ ...prev, [teamAbv]: doc }));
        } catch { /* swallow */ }
      }
    })();
    return () => { cancelled = true; };
  }, [bvpOverridePitchers, pitchesYear]);

  // Central handler for the bullpen dropdown across both views (BvP + All).
  // `reliever` is a bullpen-list entry ({ id, name, hand, ... }) or null to
  // reset to the probable pitcher. Wipes the per-team stats + pitcher-data
  // slots so the fetch effects re-pull fresh data for the new selection.
  const handleBullpenSelect = (teamAbv, reliever) => {
    const clearSlot = (setter) => setter(prev => {
      const next = { ...prev }; delete next[teamAbv]; return next;
    });
    if (!reliever) {
      clearSlot(setBvpOverridePitchers);
      clearSlot(setBvpOverrideStats);
      clearSlot(setBvpOverridePitcherData);
      return;
    }
    setBvpOverridePitchers(prev => ({
      ...prev,
      [teamAbv]: { id: reliever.id, name: reliever.name, hand: reliever.hand || '?' },
    }));
    clearSlot(setBvpOverrideStats);
    clearSlot(setBvpOverridePitcherData);
  };

  // Clear at-bat detail when matchups view changes
  useEffect(() => {
    setMatchupAtBatBatter(null);
    setMatchupAtBatData(null);
  }, [matchupsView]);

  // Lock body scroll when matchups panel is open on desktop
  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile]);

  // Fetch pitches data when switching to pitches view
  useEffect(() => {
    if ((matchupsView !== 'pitches' && matchupsView !== 'last' && matchupsView !== 'all' && matchupsView !== 'k') || !matchupsData || !visibleGameId) {
      return;
    }

    // Wait until matchupsData is for the current game before using its pitcher IDs
    if (matchupsDataGameId !== visibleGameId) {
      return;
    }

    // Skip re-fetch if we already have data for this game+year
    const effectiveFetchYear = matchupsView === 'last' ? String(new Date().getFullYear()) : pitchesYear;
    if (pitchesFetchedGameRef.current === `${visibleGameId}_${effectiveFetchYear}` && pitchesData) {
      return;
    }

    // Reset pitch indices when fetching a new game
    setPitchIdx0(0);
    setPitchIdx1(0);
    setSplitPitchIdx({});

    const awayPitcherId = matchupsData.away?.pitcher?.id;
    const homePitcherId = matchupsData.home?.pitcher?.id;
    if (!awayPitcherId && !homePitcherId) return;

    // Collect batter IDs from odds data (read from ref to avoid re-triggering on polls)
    const oddsPlayers = playersByGameIdRef.current?.[visibleGameId]?.players || [];
    const awayTeamName = visibleGame?.away || matchupsData.home?.team;
    const homeTeamName = visibleGame?.home || matchupsData.away?.team;

    // Filter to non-pitcher players (real player IDs, not team/totals entries)
    const oddsBatters = oddsPlayers.filter(p => {
      const id = String(p.playerId || '');
      // Skip synthetic IDs (team_, totals_, spreads_, etc.)
      if (id.startsWith('team_') || id.startsWith('totals_') || id.startsWith('spreads_')) return false;
      // Skip pitchers (from pitcher_strikeouts market or position-based)
      if (p.position === 'pitcher' || p.position === 'P') return false;
      return true;
    });

    // Deduplicate by playerId
    const batterIdSet = new Set();
    const uniqueBatters = [];
    oddsBatters.forEach(p => {
      const id = String(p.playerId).replace(/^mlb_/, '');
      if (!batterIdSet.has(id)) {
        batterIdSet.add(id);
        uniqueBatters.push(p);
      }
    });

    // Split by team — match against team abbreviation from odds data
    const awayBatters = uniqueBatters.filter(p => {
      const t = p.team || p.teamAbv;
      return t === awayTeamName || t === visibleGame?.awayTeam || t === 'away';
    });
    const homeBatters = uniqueBatters.filter(p => {
      const t = p.team || p.teamAbv;
      return t === homeTeamName || t === visibleGame?.homeTeam || t === 'home';
    });

    // Strip mlb_ prefix for API calls but keep original IDs for mapping
    const allBatterIds = uniqueBatters.map(p => String(p.playerId));
    const stripPrefix = (id) => id.replace(/^mlb_/, '');

    let cancelled = false;
    const fetchPitchesData = async () => {
      setPitchesLoading(true);
      try {
        const fetches = [];

        // Fetch pitcher data — "last" view always uses current calendar year
        const effectiveYear = matchupsView === 'last' ? String(new Date().getFullYear()) : pitchesYear;
        const yearParam = effectiveYear ? `?season=${effectiveYear}` : '';
        if (awayPitcherId) fetches.push(fetch(`/api/stats/mlb/atbats/pitcher/${stripPrefix(String(awayPitcherId))}${yearParam}`).then(r => r.ok ? r.json() : null).catch(() => null));
        else fetches.push(Promise.resolve(null));
        if (homePitcherId) fetches.push(fetch(`/api/stats/mlb/atbats/pitcher/${stripPrefix(String(homePitcherId))}${yearParam}`).then(r => r.ok ? r.json() : null).catch(() => null));
        else fetches.push(Promise.resolve(null));

        // Fetch batter data for all players with odds (strip mlb_ prefix for API)
        const strippedBatterIds = allBatterIds.map(stripPrefix);
        const batterFetches = strippedBatterIds.map(id =>
          fetch(`/api/stats/mlb/atbats/batter/${id}${yearParam}`).then(r => r.ok ? r.json() : null).catch(() => null)
        );

        const [awayPitcherData, homePitcherData, ...batterResults] = await Promise.all([...fetches, ...batterFetches]);

        if (cancelled) return;

        // Build a map keyed by stripped ID (no mlb_ prefix)
        const batterDataMap = {};
        strippedBatterIds.forEach((id, i) => {
          if (batterResults[i]) batterDataMap[id] = batterResults[i];
        });

        // Use batter API response for batting hand, fall back to game data, then odds data
        const awayBatterList = awayBatters.map(p => {
          const id = stripPrefix(String(p.playerId));
          const apiData = batterDataMap[id];
          const gameHand = getPlayerHandFromGame(id);
          return { batterId: id, batterName: p.name, batterHand: apiData?.bats || gameHand?.bats || p.bats || '?' };
        });
        const homeBatterList = homeBatters.map(p => {
          const id = stripPrefix(String(p.playerId));
          const apiData = batterDataMap[id];
          const gameHand = getPlayerHandFromGame(id);
          return { batterId: id, batterName: p.name, batterHand: apiData?.bats || gameHand?.bats || p.bats || '?' };
        });
        pitchesFetchedGameRef.current = `${visibleGameId}_${effectiveYear}`;
        setPitchesData({
          awayPitcher: awayPitcherData,
          homePitcher: homePitcherData,
          batterData: batterDataMap,
          awayBatters: awayBatterList,
          homeBatters: homeBatterList
        });
      } catch (error) {
        console.error('Pitches fetch error:', error);
        // Don't clear existing data on error — keep stale data visible
      } finally {
        if (!cancelled) setPitchesLoading(false);
      }
    };

    fetchPitchesData();
    return () => { cancelled = true; };
  }, [matchupsView, visibleGameId, matchupsData, matchupsDataGameId, pitchesYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch dinger report when view switches to 'dinger'
  useEffect(() => {
    if (matchupsView !== 'dinger') return;
    const now = new Date();
    const gameDateKey = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    let cancelled = false;
    const fetchDinger = async () => {
      setDingerLoading(true);
      try {
        const res = await fetch(`/api/stats/mlb/matchups/hr-scores/${gameDateKey}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setDingerData(data.hrScores || []);
        }
      } catch {
        if (!cancelled) setDingerData([]);
      } finally {
        if (!cancelled) setDingerLoading(false);
      }
    };
    fetchDinger();
    return () => { cancelled = true; };
  }, [matchupsView]);

  // Derive teams from matchupsData, enriching pitcher/batter hand info from game data
  const awayTeam = matchupsData?.away;
  const homeTeam = matchupsData?.home;
  if (awayTeam?.pitcher && !awayTeam.pitcher.pitcherHand) {
    const hand = getPlayerHandFromGame(awayTeam.pitcher.id);
    if (hand?.throws) awayTeam.pitcher.pitcherHand = hand.throws;
  }
  if (homeTeam?.pitcher && !homeTeam.pitcher.pitcherHand) {
    const hand = getPlayerHandFromGame(homeTeam.pitcher.id);
    if (hand?.throws) homeTeam.pitcher.pitcherHand = hand.throws;
  }
  // Enrich batter hands from game data
  [awayTeam, homeTeam].forEach(team => {
    team?.matchups?.forEach(m => {
      if (!m.batterHand) {
        const hand = getPlayerHandFromGame(m.batterId);
        if (hand?.bats) m.batterHand = hand.bats;
      }
    });
  });

  // BvP records don't always carry a `bbe` count — the mlb_matchups
  // collection predates the field. When it's missing, derive it from the
  // fields we do have. The identity is:
  //   BBE = at-bats that ended with contact + sac flies
  //       = (AB − K) + SF
  // Falls back to legacy `bip` when neither `bbe` nor the AB/K/SF combo is
  // available. Returns 0 for entirely-empty stats.
  const inferBbe = (s) => {
    if (!s) return 0;
    if (typeof s.bbe === 'number') return s.bbe;
    if (typeof s.ab === 'number' || typeof s.k === 'number' || typeof s.sf === 'number') {
      const ab = s.ab || 0;
      const k = s.k || 0;
      const sf = s.sf || 0;
      return Math.max(0, ab - k + sf);
    }
    if (typeof s.bip === 'number') return s.bip;
    return 0;
  };

  // Normalize atbats/matchup response into the m.totals shape that the BvP
  // renderers expect (rate fields + xbh). The endpoint gives raw counts +
  // some rates but not the derived ones the table consumes directly.
  const normalizeOverrideBvpStats = (s) => {
    if (!s) return null;
    const bbe = inferBbe(s);
    return {
      ...s,
      bbe,
      xbh: (s.doubles || 0) + (s.triples || 0) + (s.hr || 0),
      hardHitRate: bbe > 0 ? (s.hardHits || 0) / bbe : 0,
      barrelRate: bbe > 0 ? (s.barrels || 0) / bbe : 0,
    };
  };

  // Ensure any BvP stats object we hand to the renderers has a valid `bbe`
  // (either the stored value or a derivation). Preserves everything else so
  // rate fields and totals shape are untouched.
  const withInferredBbe = (s) => {
    if (!s) return s;
    return typeof s.bbe === 'number' ? s : { ...s, bbe: inferBbe(s) };
  };

  // Resolve stats object for a matchup entry based on selected bvpYear.
  // When `teamAbv` is provided and a bullpen override is active for that team,
  // the batter's stats vs. the reliever (fetched into bvpOverrideStats) take
  // precedence over the probable-pitcher `m.totals`.
  const getBvpStats = (m, teamAbv) => {
    if (teamAbv && bvpOverridePitchers[teamAbv]) {
      // Override store keys are stripped-of-mlb_; batter rows can come in
      // either form (matchupsData vs lineup extras), so strip on lookup too.
      const key = String(m.batterId).replace(/^mlb_/, '');
      const override = bvpOverrideStats[teamAbv]?.[key];
      if (override) return normalizeOverrideBvpStats(override);
    }
    if (bvpYear === 'all') return withInferredBbe(m.totals);
    return withInferredBbe(m.seasons?.[bvpYear] || null);
  };

  const handleBatterClick = async (batterId, batterName, batterHand, pitcherId, pitcherName) => {
    // Toggle off if same batter clicked again
    if (matchupAtBatBatter?.batterId === batterId) {
      setMatchupAtBatBatter(null);
      setMatchupAtBatData(null);
      setMatchupAtBatExpandedIdx(null);
      return;
    }
    setMatchupAtBatBatter({ batterId, batterName, batterHand, pitcherId, pitcherName });
    setMatchupAtBatData(null);
    setMatchupAtBatLoading(true);
    setMatchupAtBatExpandedIdx(null);
    try {
      const cleanBatterId = String(batterId).replace(/^mlb_/, '');
      const cleanPitcherId = String(pitcherId).replace(/^mlb_/, '');
      const response = await fetch(`/api/stats/mlb/atbats/matchup/${cleanBatterId}/${cleanPitcherId}`);
      if (response.ok) {
        const data = await response.json();
        setMatchupAtBatData(data);
      }
    } catch (error) {
      console.error('At-bat matchup fetch error:', error);
    } finally {
      setMatchupAtBatLoading(false);
    }
  };

  const renderAtBatDetail = () => {
    if (!matchupAtBatBatter) return null;
    const { batterName, batterHand, pitcherName } = matchupAtBatBatter;
    const atBats = [...(matchupAtBatData?.atBats || [])].sort((a, b) => (b.officialDate || '').localeCompare(a.officialDate || ''));

    // Summary stats
    const summary = { ab: 0, h: 0, hr: 0, k: 0, bb: 0 };
    atBats.forEach(ab => {
      const event = (ab.result?.event || '').toLowerCase();
      summary.ab++;
      if (/single|double|triple|home run/.test(event)) summary.h++;
      if (/home run/.test(event)) summary.hr++;
      if (/strikeout/.test(event)) summary.k++;
      if (/walk|hit by pitch|intentional/.test(event)) summary.bb++;
    });
    const avg = summary.ab > 0 ? (summary.h / summary.ab).toFixed(3).replace(/^0\./, '.') : '.000';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>
            {batterName} <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>({batterHand})</span>
            <span style={{ color: '#ffc107', fontSize: '0.9rem' }}> vs </span>
            {pitcherName}
          </span>
          <button onClick={() => { setMatchupAtBatBatter(null); setMatchupAtBatData(null); setMatchupAtBatExpandedIdx(null); setMatchupAtBatViewMode('table'); }} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px' }}>×</button>
        </div>

        {matchupAtBatLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-warning" role="status" />
          </div>
        ) : atBats.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>No at-bat history found</div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto' }}>
            {/* Summary row */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', padding: '10px 14px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
              {[
                { label: 'AB', value: summary.ab },
                { label: 'H', value: summary.h },
                { label: 'HR', value: summary.hr, color: summary.hr > 0 ? '#ffc107' : '#dee2e6' },
                { label: 'K', value: summary.k },
                { label: 'BB', value: summary.bb },
                { label: 'AVG', value: avg, color: parseFloat(avg) >= 0.300 ? '#28a745' : '#dee2e6' }
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontSize: '0.95rem', color: s.color || '#dee2e6', fontWeight: 600 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* View toggle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 14px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', gap: '4px' }}>
              {['table', 'zone'].map(mode => (
                <button key={mode} onClick={() => setMatchupAtBatViewMode(mode)} style={{ background: matchupAtBatViewMode === mode ? '#ffc107' : 'transparent', color: matchupAtBatViewMode === mode ? '#000' : '#adb5bd', border: '1px solid #444', borderRadius: '4px', padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>{mode === 'table' ? 'At-Bats' : 'Zone'}</button>
              ))}
            </div>

            {matchupAtBatViewMode === 'table' && isMobile ? (
              /* Mobile: two-line card layout */
              <div style={{ padding: '4px 6px' }}>
                {(() => {
                  let lastYear = null;
                  return atBats.map((ab, i) => {
                    const event = ab.result?.event || '-';
                    const isHit = /single|double|triple/i.test(event) && !/home run/i.test(event);
                    const isHR = /home run/i.test(event);
                    const isWalk = /walk|hit by pitch|intentional/i.test(event);
                    const isOut = !isHit && !isHR && !isWalk && event !== '-';
                    const resultColor = isHR ? '#ffc107' : isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : '#fff';
                    const count = ab.finalCount ? `${ab.finalCount.balls}-${ab.finalCount.strikes}` : '-';
                    const lastPitchEvent = ab.playEvents?.[ab.playEvents.length - 1];
                    const lastPitch = (lastPitchEvent?.details?.type?.description || lastPitchEvent?.pitchType || '-').replace(/^Four-Seam/, '4-Seam');
                    const velo = lastPitchEvent?.pitchData?.startSpeed ?? lastPitchEvent?.startSpeed ?? null;
                    const spin = lastPitchEvent?.pitchData?.breaks?.spinRate ?? lastPitchEvent?.spinRate ?? null;
                    const hitEvent = ab.playEvents?.find(pe => pe.hitData);
                    const hitData = hitEvent?.hitData;
                    const ev = hitData?.launchSpeed != null ? `${hitData.launchSpeed}` : '';
                    const la = hitData?.launchAngle != null ? `${hitData.launchAngle}\u00B0` : '';
                    const dist = hitData?.totalDistance != null ? `${hitData.totalDistance}'` : '';
                    const abYear = ab.officialDate ? ab.officialDate.slice(0, 4) : null;
                    const dateStr = ab.officialDate ? new Date(ab.officialDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-';
                    const hasPitches = ab.playEvents?.length > 0;
                    const isExpanded = matchupAtBatExpandedIdx === i;
                    const showYearSeparator = abYear && abYear !== lastYear && lastYear !== null;
                    lastYear = abYear || lastYear;
                    return (
                      <React.Fragment key={i}>
                        {showYearSeparator && <div style={{ height: '1px', backgroundColor: '#444', margin: '4px 0' }} />}
                        {(() => {
                          const evNum = ev ? parseFloat(ev) : null;
                          const laNum = hitData?.launchAngle;
                          const isBarrel = (() => {
                            if (evNum == null || laNum == null) return false;
                            if (evNum < 98) return false;
                            const minLA = Math.max(8, 26 - (evNum - 98));
                            const maxLA = Math.min(50, 30 + (evNum - 98));
                            return laNum >= minLA && laNum <= maxLA;
                          })();
                          const isHardHit = !isBarrel && evNum != null && evNum >= 95;
                          return (
                            <div
                              style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: hasPitches ? 'pointer' : 'default', backgroundColor: isExpanded ? 'rgba(96,165,250,0.18)' : (i % 2 === 0 ? '#1e2228' : '#0f1115'), gap: '8px' }}
                              onClick={() => hasPitches && setMatchupAtBatExpandedIdx(isExpanded ? null : i)}
                            >
                              {/* Col 1: Date / Inning */}
                              <div style={{ width: '52px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <div style={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>{dateStr}</div>
                                {ab.inning != null && <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>{(() => { const n = ab.inning; const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); })()}</div>}
                              </div>

                              {/* Col 2: Pitch / Velo+Spin (centered) — takes extra space */}
                              <div style={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                <div style={{ color: '#dee2e6', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', width: '100%' }}>{lastPitch}</div>
                                <div style={{ display: 'flex', gap: '10px', fontSize: '0.72rem', color: '#9CA3AF', justifyContent: 'center' }}>
                                  {velo != null && <span><span style={{ color: '#6b7280' }}>Velo</span> {Math.round(velo)}</span>}
                                  {spin != null && <span><span style={{ color: '#6b7280' }}>Spin</span> {spin}</span>}
                                </div>
                              </div>

                              {/* Col 3: Count */}
                              <div style={{ width: '32px', flexShrink: 0, textAlign: 'center', color: hasPitches ? '#93c5fd' : '#fff', fontSize: '0.85rem', fontWeight: 500 }}>{count}</div>

                              {/* Col 4: Distance */}
                              <div style={{ width: '44px', flexShrink: 0, textAlign: 'center', color: '#9CA3AF', fontSize: '0.8rem' }}>{dist || '-'}</div>

                              {/* Col 5: Result (wrapped in badge if barrel/hard hit) */}
                              <div style={{ width: '90px', flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                {isBarrel ? (
                                  <span style={{ backgroundColor: 'green', color: 'yellow', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.88rem' }}>{event}</span>
                                ) : isHardHit ? (
                                  <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.88rem' }}>{event}</span>
                                ) : (
                                  <span style={{ color: resultColor, fontWeight: 700, fontSize: '0.92rem' }}>{event}</span>
                                )}
                                {(ev || la) && !isBarrel && !isHardHit && (
                                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    {ev && `${ev}`}{la && ` · ${la}`}
                                  </span>
                                )}
                                {renderPlayButton(ab)}
                              </div>
                            </div>
                          );
                        })()}
                        {isExpanded && (
                          <div style={{ border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
                            {(() => {
                              const gamePk = ab.gamePk || ab.game_pk;
                              const atBatIdx = ab.atBatIndex ?? ab.at_bat_index;
                              const hasHit = ab.playEvents?.some(pe => pe.hitData);
                              if (!gamePk || atBatIdx == null || !hasHit) return null;
                              const szn = ab.officialDate ? ab.officialDate.slice(0, 4) : String(new Date().getFullYear());
                              return (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 12px' }}>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setBallFlightPlay({ playId: `${gamePk}_${atBatIdx}`, season: szn }); }}
                                    style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '6px', padding: '8px 20px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                                  >
                                    <span>&#x25B6;</span> Ball Flight
                                  </button>
                                </div>
                              );
                            })()}
                            <PitchZoneGrid pitchEvents={ab.playEvents} large mobile />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            ) : matchupAtBatViewMode === 'zone' ? (
              /* Zone view — one zone grid per at-bat */
              <div style={{ padding: '6px' }}>
                {(() => {
                  let lastYear = null;
                  return atBats.map((ab, i) => {
                    const event = ab.result?.event || '-';
                    const isHit = /single|double|triple/i.test(event) && !/home run/i.test(event);
                    const isHR = /home run/i.test(event);
                    const isWalk = /walk|hit by pitch|intentional/i.test(event);
                    const isOut = !isHit && !isHR && !isWalk && event !== '-';
                    const resultColor = isHR ? '#ffc107' : isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : '#fff';
                    const dateStr = ab.officialDate ? new Date(ab.officialDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-';
                    const abYear = ab.officialDate ? ab.officialDate.slice(0, 4) : null;
                    const showYearSeparator = abYear && abYear !== lastYear && lastYear !== null;
                    lastYear = abYear || lastYear;
                    const hasPitches = ab.playEvents?.length > 0;
                    return (
                      <React.Fragment key={i}>
                        {showYearSeparator && (
                          <div style={{ height: '1px', backgroundColor: '#444', margin: '4px 0' }} />
                        )}
                        <div style={{ marginBottom: '6px', borderBottom: '1px solid rgba(51,51,51,0.5)', paddingBottom: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 4px', fontSize: '0.75rem', marginBottom: '2px' }}>
                            <span style={{ color: '#9CA3AF' }}>{dateStr}</span>
                            <span style={{ color: resultColor, fontWeight: isHit || isHR ? 'bold' : 'normal' }}>{event}</span>
                          </div>
                          {hasPitches ? (
                            <PitchZoneGrid pitchEvents={ab.playEvents} large />
                          ) : (
                            <div style={{ padding: '8px', textAlign: 'center', color: '#6c757d', fontSize: '0.7rem' }}>No pitch data</div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            ) : (
            /* At-bat table */
            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Result', 'Count', 'Last Pitch', 'Velo', 'Spin', 'EV', 'LA', 'Dist', 'BRL', ''].map(col => (
                    <th key={col || 'play'} style={{ color: '#6b7280', padding: '5px 5px', borderBottom: '1px solid #333', textAlign: col === 'Result' || col === 'Last Pitch' ? 'left' : 'center', fontSize: '0.75rem', fontWeight: 600 }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let lastYear = null;
                  return atBats.map((ab, i) => {
                  const event = ab.result?.event || '-';
                  const isHit = /single|double|triple/i.test(event) && !/home run/i.test(event);
                  const isHR = /home run/i.test(event);
                  const isWalk = /walk|hit by pitch|intentional/i.test(event);
                  const isOut = !isHit && !isHR && !isWalk && event !== '-';
                  const resultColor = isHR ? '#ffc107' : isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : '#fff';

                  const count = ab.finalCount ? `${ab.finalCount.balls}-${ab.finalCount.strikes}` : '-';
                  const lastPitchEvent = ab.playEvents?.[ab.playEvents.length - 1];
                  const lastPitch = (lastPitchEvent?.details?.type?.description || lastPitchEvent?.pitchType || '-').replace(/^Four-Seam/, '4-Seam');
                  const velo = lastPitchEvent?.pitchData?.startSpeed ?? lastPitchEvent?.startSpeed ?? null;
                  const spin = lastPitchEvent?.pitchData?.breaks?.spinRate ?? lastPitchEvent?.spinRate ?? null;

                  const hitEvent = ab.playEvents?.find(pe => pe.hitData);
                  const hitData = hitEvent?.hitData;
                  const ev = hitData?.launchSpeed != null ? `${hitData.launchSpeed}` : '';
                  const la = hitData?.launchAngle != null ? `${hitData.launchAngle}\u00B0` : '';
                  const dist = hitData?.totalDistance != null ? `${hitData.totalDistance}'` : '';
                  const isBarrel = ab.isBarrel || (() => {
                    if (!hitData?.launchSpeed || !hitData?.launchAngle) return false;
                    const speed = hitData.launchSpeed;
                    const angle = hitData.launchAngle;
                    if (speed < 98) return false;
                    const minLA = Math.max(8, 26 - (speed - 98));
                    const maxLA = Math.min(50, 30 + (speed - 98));
                    return angle >= minLA && angle <= maxLA;
                  })();

                  const abYear = ab.officialDate ? ab.officialDate.slice(0, 4) : null;
                  const dateStr = ab.officialDate ? new Date(ab.officialDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-';
                  const hasPitches = ab.playEvents?.length > 0;
                  const isExpanded = matchupAtBatExpandedIdx === i;

                  const showYearSeparator = abYear && abYear !== lastYear && lastYear !== null;
                  lastYear = abYear || lastYear;

                  return (
                    <React.Fragment key={i}>
                      {showYearSeparator && (
                        <tr>
                          <td colSpan={11} style={{ padding: 0, height: '1px', backgroundColor: '#444' }} />
                        </tr>
                      )}
                      <tr style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', cursor: hasPitches ? 'pointer' : 'default' }} onClick={() => hasPitches && setMatchupAtBatExpandedIdx(isExpanded ? null : i)}>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: '#9CA3AF' }}>{dateStr}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'left', color: resultColor, fontWeight: isHit || isHR ? 'bold' : 'normal' }}>{event}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: isExpanded ? '#60a5fa' : hasPitches ? '#93c5fd' : '#fff', textDecoration: hasPitches ? 'underline' : 'none', textDecorationColor: '#4b5563' }}>{count}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'left', color: '#9CA3AF' }}>{lastPitch}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: '#9CA3AF' }}>{velo != null ? `${Math.round(velo)}` : '-'}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: '#9CA3AF' }}>{spin != null ? spin : '-'}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: ev && parseFloat(ev) >= 95 ? '#F59E0B' : '#9CA3AF' }}>{ev || '-'}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: '#fff' }}>{la || '-'}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center', color: '#fff' }}>{dist || '-'}</td>
                        <td style={{ padding: '4px 5px', textAlign: 'center' }}>
                          {isBarrel ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2 2L5 5M5 5L3.5 6.5C3 7 3 7.8 3.5 8.3L15.7 20.5C16.2 21 17 21 17.5 20.5L20.5 17.5C21 17 21 16.2 20.5 15.7L8.3 3.5C7.8 3 7 3 6.5 3.5L5 5Z"/>
                            </svg>
                          ) : hitData ? (
                            <span style={{ color: '#4b5563' }}>-</span>
                          ) : ''}
                        </td>
                        <td style={{ padding: '4px 5px', textAlign: 'center' }}>{renderPlayButton(ab)}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={11} style={{ padding: 0, border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
                            <PitchZoneGrid pitchEvents={ab.playEvents} large />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                });
                })()}
              </tbody>
            </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMatchupsTable = (team, opposingTeam, teamIdx, mobile) => {
    if (!team) return null;
    const teamAbv = team.team;
    const overridePitcher = bvpOverridePitchers[teamAbv] || null;
    // Effective pitcher: bullpen selection wins over the probable pitcher so
    // the header, BvP data lookups, and downstream batter-click handlers all
    // point at the reliever the user chose.
    const pitcher = overridePitcher
      ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
      : team.pitcher;
    const fontSize = mobile ? '0.7rem' : '0.85rem';
    const stickyTop = mobile ? 34 : 36;

    // Bullpen list — resolves IDs → names/hand/rank via the shared helper.
    const isHomePitcher = team?.team === visibleGame?.home;
    const bullpenList = buildBullpenList(visibleGame, isHomePitcher);

    return (
      <div key={teamIdx} style={{ flex: mobile ? 'none' : 1, borderRight: !mobile && teamIdx === 0 ? '1px solid #333' : 'none', borderBottom: mobile && teamIdx === 0 ? '1px solid #444' : 'none', overflow: mobile ? 'visible' : 'auto' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', position: mobile ? 'static' : 'sticky', top: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam.team}</span>
            {pitcher && (
              <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
              </span>
            )}
            <BullpenDropdown
              bullpenList={bullpenList}
              selectedId={overridePitcher?.id}
              onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
              mobile={mobile}
            />
          </div>
        </div>
        <div style={{ overflowX: mobile ? 'auto' : 'visible' }}>
        <table style={{ width: '100%', fontSize, borderCollapse: 'collapse', minWidth: mobile ? '540px' : 'auto' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #333', color: '#adb5bd' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>Batter</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1, width: '32px' }} title="Sample size — circle grows with plate appearances"></th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>AVG</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>SLG</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>HH%</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>Brl%</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>PA</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>BBE</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>H</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>HR</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>XBH</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>K</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>BB</th>
              {!mobile && <th style={{ padding: '6px 4px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: stickyTop, backgroundColor: '#212529', zIndex: 1 }}>OPS</th>}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const matchupBatters = team.matchups || [];
              const matchupIds = new Set(matchupBatters.map(m => String(m.batterId).replace(/^mlb_/, '')));
              // Get lineup batters from odds data not already in matchups
              const oddsPlayers = playersByGameIdRef.current?.[visibleGameId]?.players || [];
              // Lineup extras come from the batting team (opposingTeam)
              const battingTeamAbv = opposingTeam?.team;
              const lineupExtras = oddsPlayers.filter(p => {
                const id = String(p.playerId || '');
                if (id.startsWith('team_') || id.startsWith('totals_') || id.startsWith('spreads_')) return false;
                if (p.position === 'pitcher' || p.position === 'P') return false;
                const t = p.team || p.teamAbv;
                if (t !== battingTeamAbv) return false;
                const stripped = id.replace(/^mlb_/, '');
                return !matchupIds.has(stripped);
              });
              // Deduplicate extras
              const seenIds = new Set();
              const dedupedExtras = lineupExtras.filter(p => {
                const id = String(p.playerId).replace(/^mlb_/, '');
                if (seenIds.has(id)) return false;
                seenIds.add(id);
                return true;
              });
              const allBatters = [
                ...matchupBatters.map(m => ({ ...m, hasHistory: true })),
                ...dedupedExtras.map(p => { const id = String(p.playerId).replace(/^mlb_/, ''); return { batterId: id, batterName: p.name || p.playerName, batterHand: p.bats || getPlayerHandFromGame(id)?.bats || '?', hasHistory: false }; })
              ];
              // Sort by PA vs this pitcher (descending) — most-faced batters first.
              // Tiebreak on batting order so equal-PA rows stay in lineup order.
              allBatters.sort((a, b) => {
                const paA = a.hasHistory ? (getBvpStats(a, teamAbv)?.pa || 0) : 0;
                const paB = b.hasHistory ? (getBvpStats(b, teamAbv)?.pa || 0) : 0;
                if (paB !== paA) return paB - paA;
                return getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx);
              });
              if (allBatters.length === 0) return (
                <tr><td colSpan={mobile ? 13 : 14} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No matchup data</td></tr>
              );
              return allBatters.map((m, i) => {
                // When a bullpen override is active, treat every batter as
                // "has history" (even zero-PA) so the table doesn't silently
                // filter them out — an empty row still conveys "no history".
                const overrideActive = !!bvpOverridePitchers[teamAbv];
                const s = overrideActive ? getBvpStats(m, teamAbv) : (m.hasHistory ? getBvpStats(m, teamAbv) : null);
                const isSelected = matchupAtBatBatter?.batterId === m.batterId;
                const batterCellStyle = { padding: '6px 8px', color: isSelected ? '#000' : '#fff', whiteSpace: 'nowrap', cursor: pitcher ? 'pointer' : 'default', backgroundColor: isSelected ? '#ffc107' : 'transparent' };
                const onBatterClick = pitcher ? () => handleBatterClick(m.batterId, m.batterName, m.batterHand, pitcher.id, pitcher.name) : undefined;
                if (!s || !s.pa) return (
                  <tr key={m.batterId} style={{ borderBottom: '1px solid #2a2d31', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', height: '38px' }}>
                    <td style={batterCellStyle} onClick={onBatterClick} onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}>
                      {renderBatOrder(m.batterId, teamIdx)}{m.batterName} <span style={{ color: isSelected ? '#555' : '#6c757d', fontSize: '0.65rem' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                    </td>
                    <td colSpan={mobile ? 12 : 13} style={{ padding: '6px 4px', textAlign: 'center', color: '#555' }}>-</td>
                  </tr>
                );
                return (
                  <tr key={m.batterId} style={{ borderBottom: '1px solid #2a2d31', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', height: '38px' }}>
                    <td style={batterCellStyle} onClick={onBatterClick} onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}>
                      {renderBatOrder(m.batterId, teamIdx)}{m.batterName} <span style={{ color: isSelected ? '#555' : '#6c757d', fontSize: '0.65rem' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                    </td>
                    {(() => {
                      const pa = s.pa || 0;
                      // Sample-size signal bars — 1–5 vertical bars light up
                      // based on PA: 1-2→1, 3-5→2, 6-7→3, 8-9→4, 10+→5.
                      // Pure shape cue, works regardless of color vision.
                      const BAR_THRESHOLDS = [1, 3, 6, 8, 10];
                      const lit = BAR_THRESHOLDS.filter(t => pa >= t).length;
                      return (
                        <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle' }} title={pa > 0 ? `${pa} PA vs this pitcher` : 'No history vs this pitcher'}>
                          {pa > 0 && (
                            <svg width="26" height="20" viewBox="0 0 26 20" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                              {[1, 2, 3, 4, 5].map(bar => {
                                const h = 4 + (bar - 1) * 3.5;
                                const x = 1.5 + (bar - 1) * 5;
                                const y = 18 - h;
                                return (
                                  <rect
                                    key={bar}
                                    x={x}
                                    y={y}
                                    width="3.5"
                                    height={h}
                                    rx="0.75"
                                    fill={bar <= lit ? '#9CA3AF' : '#1f2937'}
                                  />
                                );
                              })}
                            </svg>
                          )}
                        </td>
                      );
                    })()}
                    {(() => {
                      const avg = s.avg || 0;
                      return <td style={{ padding: '6px 4px', textAlign: 'center' }}>{avg >= 0.350 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{avg.toFixed(3).replace(/^0\./, '.')}</span> : avg >= 0.300 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{avg.toFixed(3).replace(/^0\./, '.')}</span> : avg >= 0.275 ? <span style={{ color: '#F59E0B' }}>{avg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{avg.toFixed(3).replace(/^0\./, '.')}</span>}</td>;
                    })()}
                    {(() => {
                      const slg = s.slg || 0;
                      return <td style={{ padding: '6px 4px', textAlign: 'center' }}>{slg >= 0.550 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{slg.toFixed(3).replace(/^0\./, '.')}</span> : slg >= 0.450 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{slg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{slg.toFixed(3).replace(/^0\./, '.')}</span>}</td>;
                    })()}
                    <td style={{ padding: mobile ? '6px 2px' : '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{(s.hardHitRate || 0) >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: mobile ? '2px 3px' : '2px 6px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', width: mobile ? undefined : '55px', minWidth: mobile ? '38px' : undefined, textAlign: 'center' }}>{Math.round((s.hardHitRate || 0) * 100)}%</span> : (s.hardHitRate || 0) >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: mobile ? '2px 3px' : '2px 6px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', width: mobile ? undefined : '55px', minWidth: mobile ? '38px' : undefined, textAlign: 'center' }}>{Math.round((s.hardHitRate || 0) * 100)}%</span> : `${Math.round((s.hardHitRate || 0) * 100)}%`}</td>
                    <td style={{ padding: mobile ? '6px 2px' : '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{(s.barrelRate || 0) >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: mobile ? '2px 3px' : '2px 6px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', width: mobile ? undefined : '55px', minWidth: mobile ? '38px' : undefined, textAlign: 'center' }}>{Math.round((s.barrelRate || 0) * 100)}%</span> : (s.barrelRate || 0) >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: mobile ? '2px 3px' : '2px 6px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', width: mobile ? undefined : '55px', minWidth: mobile ? '38px' : undefined, textAlign: 'center' }}>{Math.round((s.barrelRate || 0) * 100)}%</span> : `${Math.round((s.barrelRate || 0) * 100)}%`}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{s.pa || 0}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{s.bbe || 0}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{s.h || 0}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: (s.hr || 0) > 0 ? '#ffc107' : '#dee2e6' }}>{s.hr || 0}</td>
                    {(() => {
                      const xbh = s.xbh ?? ((s.doubles || 0) + (s.triples || 0) + (s.hr || 0));
                      return <td style={{ padding: '6px 4px', textAlign: 'center', color: xbh > 0 ? '#ffc107' : '#dee2e6' }}>{xbh}</td>;
                    })()}
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{s.k || 0}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center', color: '#dee2e6' }}>{s.bb || 0}</td>
                    {!mobile && <td style={{ padding: '6px 4px', textAlign: 'center', color: (s.ops || 0) >= 0.800 ? '#28a745' : '#dee2e6' }}>{(s.ops || 0).toFixed(3).replace(/^0\./, '.')}</td>}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  // Pitch code to display name
  const pitchCodeName = { FF: '4-Seam', SI: 'Sinker', FC: 'Cutter', CU: 'Curve', SL: 'Slider', CH: 'Change', ST: 'Sweeper', KC: 'Knuckle CB', FS: 'Splitter', KN: 'Knuckle' };

  // Column definitions — Pitches Detail screen always shows the combined Trad columns
  // (the Trad/Adv toggle still applies in Summary mode, via renderPitchSummary).
  const isAdv = false;
  const pitchCols = { name: '28%', c1: '7%', c2: '8%', c3: '8%', c4: '7%', c5: '8%', c6: '9%', c7: '9%', c8: '8%', c9: '8%' };
  const colCount = 10;

  // Shared table header for pitches tables
  const renderPitchTableHeader = (label) => (
    <thead>
      <tr style={{ borderBottom: '1px solid #333', color: '#adb5bd' }}>
        <th style={{ padding: '6px 8px', textAlign: 'left', width: pitchCols.name }}></th>
        {isAdv ? (
          <>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c1 }}>#</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c2 }}>Swng</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c3 }}>Whiff</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c4 }}>BBE</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c5 }}>HH%</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c6 }}>Brl%</th>
          </>
        ) : (
          <>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c1 }}>#</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c2 }}>Swng</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c3 }}>Whiff</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c4 }}>BBE</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c5 }}>H</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c6 }}>AVG</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c7 }}>SLG</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c8 }}>HR</th>
            <th style={{ padding: '6px 4px', textAlign: 'center', width: pitchCols.c9 }}>XBH</th>
          </>
        )}
      </tr>
    </thead>
  );

  // Helper to compute all stats from pitch data (works for both pitcher and batter data)
  const computePitchStats = (d) => {
    if (!d) return null;
    const seen = d.seen ?? d.thrown ?? 0;
    const swings = d.swings || 0;
    const bbe = d.bbe || 0;
    const ab = d.ab ?? d.bbe ?? 0;
    const h = d.h || 0;
    const hr = d.hr || 0;
    const doubles = d.doubles || d['2b'] || 0;
    const triples = d.triples || d['3b'] || 0;
    const xbh = (doubles > 0 || triples > 0) ? (doubles + triples + hr) : (d.singles != null ? (h - d.singles) : hr);
    const avg = ab > 0 ? h / ab : 0;
    const singles = d.singles || (h - doubles - triples - hr);
    const slg = ab > 0 ? (singles + doubles * 2 + triples * 3 + hr * 4) / ab : 0;
    const whiff = d.whiffRate ?? (swings > 0 ? (d.whiffs || 0) / swings : 0);
    const hhRate = d.hardHitRate ?? (bbe > 0 ? (d.hardHits || 0) / bbe : 0);
    const brlRate = d.barrelRate ?? (bbe > 0 ? (d.barrels || 0) / bbe : 0);
    const swSpRate = d.sweetSpotRate ?? (bbe > 0 ? (d.sweetSpotBIP || 0) / bbe : 0);
    const noData = (seen === 0 && ab === 0);
    return { seen, swings, bbe, ab, h, hr, doubles, triples, xbh, avg, slg, whiff, hhRate, brlRate, swSpRate, noData };
  };

  // Render stat cells based on metric mode (for pitcher row - gold colors)
  const renderPitcherCells = (s) => {
    if (isAdv) return (
      <>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.seen}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.swings}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: s.whiff >= 0.30 ? '#28a745' : '#ffc107' }}>{Math.round(s.whiff * 100)}%</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.bbe}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.hhRate >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.hhRate * 100)}%</span> : s.hhRate >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.hhRate * 100)}%</span> : <span style={{ color: '#ffc107' }}>{Math.round(s.hhRate * 100)}%</span>}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.brlRate >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.brlRate * 100)}%</span> : s.brlRate >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.brlRate * 100)}%</span> : <span style={{ color: '#ffc107' }}>{Math.round(s.brlRate * 100)}%</span>}</td>
      </>
    );
    return (
      <>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.seen ?? '-'}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.swings ?? '-'}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: s.whiff >= 0.30 ? '#28a745' : '#ffc107' }}>{s.whiff != null ? `${Math.round(s.whiff * 100)}%` : '-'}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.bbe ?? '-'}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.h}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.avg >= 0.350 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : s.avg >= 0.300 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#ffc107' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.slg >= 0.550 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : s.slg >= 0.450 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#ffc107' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: s.hr > 0 ? '#dc3545' : '#ffc107' }}>{s.hr}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#ffc107' }}>{s.xbh}</td>
      </>
    );
  };

  // Render stat cells for batter row
  const renderBatterCells = (s, noData) => {
    const dim = '#555';
    if (isAdv) return (
      <>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : s.seen}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : s.swings}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : s.whiff >= 0.30 ? '#28a745' : '#dee2e6' }}>{noData ? '-' : `${Math.round(s.whiff * 100)}%`}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : s.bbe}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{noData ? <span style={{ color: dim }}>-</span> : s.hhRate >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.hhRate * 100)}%</span> : s.hhRate >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.hhRate * 100)}%</span> : <span style={{ color: '#dee2e6' }}>{Math.round(s.hhRate * 100)}%</span>}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{noData ? <span style={{ color: dim }}>-</span> : s.brlRate >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.brlRate * 100)}%</span> : s.brlRate >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{Math.round(s.brlRate * 100)}%</span> : <span style={{ color: '#dee2e6' }}>{Math.round(s.brlRate * 100)}%</span>}</td>
      </>
    );
    return (
      <>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : (s.seen ?? '-')}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : (s.swings ?? '-')}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : (s.whiff >= 0.30 ? '#28a745' : '#dee2e6') }}>{noData ? '-' : (s.whiff != null ? `${Math.round(s.whiff * 100)}%` : '-')}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : (s.bbe ?? '-')}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : '#dee2e6' }}>{noData ? '-' : s.h}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{noData ? <span style={{ color: dim }}>-</span> : s.avg >= 0.350 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : s.avg >= 0.300 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : s.avg >= 0.275 ? <span style={{ color: '#F59E0B' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
        <td style={{ padding: '6px 2px', textAlign: 'center' }}>{noData ? <span style={{ color: dim }}>-</span> : s.slg >= 0.550 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : s.slg >= 0.450 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 3px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '38px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : s.hr > 0 ? '#ffc107' : '#dee2e6' }}>{noData ? '-' : s.hr}</td>
        <td style={{ padding: '6px 4px', textAlign: 'center', color: noData ? dim : s.xbh > 0 ? '#ffc107' : '#dee2e6' }}>{noData ? '-' : s.xbh}</td>
      </>
    );
  };

  // Helper to render a pitcher stats row
  const renderPitcherRow = (name, hand, pitchData, fontSize, extra, pitcherId, pitcherTeam) => {
    const s = computePitchStats(pitchData);
    if (!s) return null;
    return (
      <tr style={{ borderBottom: '2px solid #ffc107', backgroundColor: 'rgba(255,193,7,0.08)', height: '38px' }}>
        <td style={{ padding: '6px 8px', color: '#ffc107', whiteSpace: 'nowrap', fontSize, fontWeight: 600 }}>
          {name} <span style={{ color: '#adb5bd', fontSize: '0.65rem', fontWeight: 400 }}>({hand}){extra ? ` ${extra}` : ''}</span>{pitcherId && renderPlayerIcon(pitcherId, name, pitcherTeam)}
        </td>
        {renderPitcherCells(s)}
      </tr>
    );
  };

  // Helper to render a batter row for pitches table
  const renderPitchBatterRow = (m, i, pitchData, fontSize, pitcher, teamIdx, batterTeam) => {
    const s = computePitchStats(pitchData);
    const noData = !s || s.noData;
    const isSelected = matchupAtBatBatter?.batterId === m.batterId;
    return (
      <tr key={m.batterId + '-' + i} style={{ borderBottom: '1px solid #2a2d31', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', height: '38px' }}>
        <td
          style={{ padding: '6px 8px', color: isSelected ? '#000' : '#fff', whiteSpace: 'nowrap', fontSize, cursor: pitcher ? 'pointer' : 'default', backgroundColor: isSelected ? '#ffc107' : 'transparent' }}
          onClick={pitcher ? () => handleBatterClick(m.batterId, m.batterName, m.batterHand, pitcher.id, pitcher.name) : undefined}
          onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, batterTeam)}
        >
          {renderBatOrder(m.batterId, teamIdx)}{m.batterName} <span style={{ color: isSelected ? '#555' : '#6c757d', fontSize: '0.65rem' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, batterTeam)}
        </td>
        {renderBatterCells(s, noData)}
      </tr>
    );
  };

  // Render pitches table for one team section
  const renderPitchesTable = (team, opposingTeam, teamIdx, mobile) => {
    if (!team || !pitchesData) return null;
    const teamAbv = team.team;
    const overridePitcher = bvpOverridePitchers[teamAbv] || null;
    // When a bullpen selection is active, swap the pitcher used for the
    // header + pitcher-arsenal lookups. Falls through to the probable
    // pitcher's fetched doc while the reliever's is still loading.
    const pitcher = overridePitcher
      ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
      : team.pitcher;
    const fontSize = mobile ? '0.7rem' : '0.85rem';

    // Bullpen list resolves via the shared helper — same source used by the
    // BvP and All views for consistency.
    const isHomePitcher = team?.team === visibleGame?.home;
    const bullpenList = buildBullpenList(visibleGame, isHomePitcher);

    // Determine which pitcher's data to show (opposing pitcher faces these batters).
    // Prefer the reliever's fetched pitcher doc when a bullpen override is
    // active for this team.
    const probablePitcherData = teamIdx === 0 ? pitchesData.homePitcher : pitchesData.awayPitcher;
    const opposingPitcherData = overridePitcher
      ? (bvpOverridePitcherData[teamAbv] || probablePitcherData)
      : probablePitcherData;
    const pitcherThrows = opposingPitcherData?.throws || pitcher?.pitcherHand;
    const byPitch = opposingPitcherData?.seasonStats?.byPitch || {};
    const totalPitches = opposingPitcherData?.seasonStats?.overall?.totalPitches || 1;

    // Build pitch list sorted by thrown count (most used first)
    const pitchList = Object.entries(byPitch)
      .map(([code, data]) => ({ code, ...data, usage: Math.round((data.thrown / totalPitches) * 100) }))
      .sort((a, b) => b.thrown - a.thrown);

    const pitchIdx = teamIdx === 0 ? pitchIdx0 : pitchIdx1;
    const setPitchIdx = teamIdx === 0 ? setPitchIdx0 : setPitchIdx1;
    const safePitchIdx = Math.max(0, Math.min(pitchIdx, pitchList.length - 1));
    const selectedPitch = pitchList[safePitchIdx];

    // Get batters from odds data (stored during fetch), fall back to matchups — sorted by batting order
    const batters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || team.matchups || [])]
      .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));

    // For splits mode: pitcher's byPitchHand stats for selected pitch
    const pitcherByPitchHand = opposingPitcherData?.seasonStats?.byPitchHand || {};

    return (
      <div key={teamIdx} style={{ flex: mobile ? 'none' : 1, borderRight: !mobile && teamIdx === 0 ? '1px solid #333' : 'none', borderBottom: mobile && teamIdx === 0 ? '1px solid #444' : 'none', overflowX: 'auto', overflowY: mobile ? 'visible' : 'auto', minWidth: 0, maxWidth: '100%' }}>
        {/* Team header */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', position: mobile ? 'static' : 'sticky', top: 0, zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam.team}</span>
            {pitcher && (
              <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
              </span>
            )}
            <BullpenDropdown
              bullpenList={bullpenList}
              selectedId={overridePitcher?.id}
              onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
              mobile={mobile}
            />
          </div>
        </div>
        {/* Pitch type selector (All mode only — Splits mode has per-hand selectors) */}
        {pitchList.length > 0 ? (
          <>
            {pitchesSplitMode === 'all' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 8px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', position: mobile ? 'static' : 'sticky', top: mobile ? 'auto' : 36, zIndex: 1, flexWrap: 'wrap' }}>
                {pitchList.map((p, i) => (
                  <button
                    key={p.code}
                    onClick={() => setPitchIdx(i)}
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      borderRadius: '3px',
                      border: 'none',
                      backgroundColor: i === safePitchIdx ? '#ffc107' : '#343a40',
                      color: i === safePitchIdx ? '#000' : '#adb5bd',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pitchCodeName[p.code] || p.code} {p.usage}%
                  </button>
                ))}
              </div>
            )}

            {pitchesSplitMode === 'all' ? (
              <>
                {/* All mode: pitcher row + all batters */}
                <table style={{ width: '100%', fontSize, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  {renderPitchTableHeader(opposingPitcherData?.playerName || opposingPitcherData?.name || pitcher?.name || 'Pitcher')}
                  <tbody>
                    {selectedPitch && renderPitcherRow(
                      opposingPitcherData?.playerName || opposingPitcherData?.name || pitcher?.name || 'Pitcher',
                      pitcherThrows,
                      selectedPitch,
                      fontSize,
                      selectedPitch.avgSpeed ? `${selectedPitch.avgSpeed} mph` : '',
                      opposingPitcherData?.id || pitcher?.id,
                      team?.team
                    )}
                    {batters.map((m, i) => {
                      const batterData = pitchesData.batterData?.[m.batterId];
                      const pitchData = batterData?.seasonStats?.byPitch?.[selectedPitch?.code];
                      return renderPitchBatterRow(m, i, pitchData, fontSize, pitcher, teamIdx, opposingTeam?.team);
                    })}
                    {batters.length === 0 && (
                      <tr><td colSpan={colCount} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No batter data</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <>
                {/* Splits mode: each hand gets its own pitch list from byPitchHand */}
                {['R', 'L'].map(hand => {
                  // Build pitch list specific to this batter hand from pitcher's byPitchHand
                  // Keys like FF_R, SL_R for vs RHB; FF_L, SL_L for vs LHB
                  const handSuffix = '_' + hand;
                  const handPitchRaw = Object.entries(pitcherByPitchHand)
                    .filter(([key]) => key.endsWith(handSuffix));
                  const handTotalPitches = handPitchRaw.reduce((sum, [, data]) => sum + (data.thrown || 0), 0) || 1;
                  const handPitchEntries = handPitchRaw
                    .map(([key, data]) => {
                      const code = key.slice(0, -2); // Remove _R or _L suffix
                      return { code, ...data, usage: Math.round(((data.thrown || 0) / handTotalPitches) * 100) };
                    })
                    .sort((a, b) => (b.thrown || 0) - (a.thrown || 0));

                  // Switch hitters bat opposite the pitcher's hand
                  const handBatters = batters.filter(m => {
                    if (m.batterHand === 'S') {
                      const switchHand = pitcherThrows === 'R' ? 'L' : 'R';
                      return hand === switchHand;
                    }
                    return m.batterHand === hand;
                  });
                  if (handPitchEntries.length === 0 && handBatters.length === 0) return null;

                  const splitKey = `${teamIdx}_${hand}`;
                  const handPitchIdx = Math.max(0, Math.min(splitPitchIdx[splitKey] || 0, handPitchEntries.length - 1));
                  const handSelectedPitch = handPitchEntries[handPitchIdx];

                  return (
                    <div key={hand}>
                      <div style={{ padding: '4px 12px', backgroundColor: '#2a2d31', borderBottom: '1px solid #333', fontSize: mobile ? '0.65rem' : '0.75rem', fontWeight: 600, color: '#ffc107' }}>
                        {pitcher?.name || 'Pitcher'} vs {hand === 'R' ? 'RHB' : 'LHB'}
                      </div>
                      {/* Per-hand pitch selector */}
                      {handPitchEntries.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', flexWrap: 'wrap' }}>
                          {handPitchEntries.map((p, i) => (
                            <button
                              key={p.code}
                              onClick={() => setSplitPitchIdx(prev => ({ ...prev, [splitKey]: i }))}
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                borderRadius: '3px',
                                border: 'none',
                                backgroundColor: i === handPitchIdx ? '#ffc107' : '#343a40',
                                color: i === handPitchIdx ? '#000' : '#adb5bd',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {pitchCodeName[p.code] || p.code} {p.usage}%
                            </button>
                          ))}
                        </div>
                      )}
                      <table style={{ width: '100%', fontSize, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        {renderPitchTableHeader(opposingPitcherData?.playerName || opposingPitcherData?.name || pitcher?.name || 'Pitcher')}
                        <tbody>
                          {handSelectedPitch && renderPitcherRow(
                            pitcher?.name || 'Pitcher',
                            pitcherThrows,
                            handSelectedPitch,
                            fontSize,
                            '',
                            opposingPitcherData?.id || pitcher?.id,
                            team?.team
                          )}
                          {handBatters.map((m, i) => {
                            const batterData = pitchesData.batterData?.[m.batterId];
                            const batterSplitKey = handSelectedPitch?.code + (pitcherThrows === 'L' ? '_L' : '_R');
                            const pitchData = batterData?.seasonStats?.byPitchHand?.[batterSplitKey] || (handSelectedPitch ? batterData?.seasonStats?.byPitch?.[handSelectedPitch.code] : null);
                            return renderPitchBatterRow(m, i, pitchData, fontSize, pitcher, teamIdx, opposingTeam?.team);
                          })}
                          {handBatters.length === 0 && (
                            <tr><td colSpan={colCount} style={{ padding: '8px', textAlign: 'center', color: '#555', fontSize: '0.75rem' }}>No {hand === 'R' ? 'RHB' : 'LHB'} in lineup</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </>
            )}
          </>
        ) : (
          <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d' }}>No pitch data for {pitcher?.name || 'this pitcher'}</div>
        )}
      </div>
    );
  };

  // Render Last X / Overall table for one team section
  // Render pitch summary view — pitches as column groups, batters as rows, stacked by team
  const renderPitchSummary = (mobile) => {
    if (!pitchesData || !matchupsData) return null;
    const fs = mobile ? '0.7rem' : '0.85rem';
    const isAdv = pitchesMetricMode === 'advanced';
    const statCols = isAdv ? ['#', 'Hard Hit', 'Barrels', 'Whiff', 'Chase'] : ['#', 'AVG', 'SLG', 'BBE', 'HR'];
    const numStats = statCols.length;

    const renderBadge = (val, greenThreshold, orangeThreshold) => {
      if (val >= greenThreshold) return <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(val * 100)}%</span>;
      if (val >= orangeThreshold) return <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(val * 100)}%</span>;
      return `${Math.round(val * 100)}%`;
    };
    // Badge variant for slash-line decimals (e.g. AVG, SLG) — formats .xxx instead of x%.
    const renderDecimalBadge = (val, greenThreshold, orangeThreshold, fallbackColor) => {
      const text = val.toFixed(3).replace(/^0\./, '.');
      if (val >= greenThreshold) return <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{text}</span>;
      if (val >= orangeThreshold) return <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{text}</span>;
      return <span style={{ color: fallbackColor }}>{text}</span>;
    };

    // Compute shared pitch count so both tables have the same columns
    const getTopPitches = (pitcherData) => {
      if (!pitcherData) return [];
      const byPitch = pitcherData.seasonStats?.byPitch || {};
      const totalPitches = pitcherData.seasonStats?.overall?.totalPitches || 1;
      return Object.entries(byPitch)
        .map(([code, data]) => ({ code, ...data, usage: Math.round((data.thrown / totalPitches) * 100) }))
        .sort((a, b) => b.thrown - a.thrown)
        .slice(0, 4);
    };
    // Hand-specific top pitches from byPitchHand (used for splits mode)
    const getTopPitchesForHand = (pitcherData, hand) => {
      if (!pitcherData || !hand) return [];
      const byPitchHand = pitcherData.seasonStats?.byPitchHand || {};
      const suffix = `_${hand}`;
      const handEntries = Object.entries(byPitchHand).filter(([key]) => key.endsWith(suffix));
      const totalHandPitches = handEntries.reduce((sum, [, data]) => sum + (data?.thrown || 0), 0) || 1;
      return handEntries
        .map(([key, data]) => ({ code: key.slice(0, -2), ...data, usage: Math.round(((data?.thrown || 0) / totalHandPitches) * 100) }))
        .sort((a, b) => (b.thrown || 0) - (a.thrown || 0))
        .slice(0, 4);
    };
    const maxPitchCols = Math.max(
      getTopPitches(pitchesData.homePitcher).length,
      getTopPitches(pitchesData.awayPitcher).length,
      1
    );

    const renderPitcherSection = (pitcherDataProp, team, opposingTeam, teamIdx) => {
      const teamAbv = team?.team;
      const overridePitcher = teamAbv ? (bvpOverridePitchers[teamAbv] || null) : null;
      const pitcher = overridePitcher
        ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
        : team?.pitcher;
      // Prefer the reliever's fetched doc when an override is active, but
      // fall through to the probable pitcher's doc while it's still loading
      // so the section doesn't briefly go empty on selection.
      const pitcherData = overridePitcher
        ? (bvpOverridePitcherData[teamAbv] || pitcherDataProp)
        : pitcherDataProp;
      const isHomePitcher = teamAbv === visibleGame?.home;
      const bullpenList = buildBullpenList(visibleGame, isHomePitcher);
      const bullpenDropdown = teamAbv ? (
        <BullpenDropdown
          bullpenList={bullpenList}
          selectedId={overridePitcher?.id}
          onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
          mobile={mobile}
        />
      ) : null;
      const topPitches = pitcherData ? getTopPitches(pitcherData) : [];

      if (!pitcherData || topPitches.length === 0) {
        return (
          <div style={{ borderBottom: '1px solid #444' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <img src={getTeamLogoUrlForSport(opposingTeam?.team)} alt={opposingTeam?.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
              <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam?.team}</span>
              {pitcher && (
                <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                  vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                </span>
              )}
              {bullpenDropdown}
            </div>
            <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d' }}>No pitch data for {pitcher?.name || 'this pitcher'}</div>
          </div>
        );
      }

      const defaultPitchStats = topPitches.map(p => ({ pitch: p, s: computePitchStats(p) }));
      while (defaultPitchStats.length < maxPitchCols) {
        defaultPitchStats.push({ pitch: { code: `empty_${defaultPitchStats.length}`, usage: 0 }, s: null });
      }
      const allBatters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || [])]
        .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));
      const pitcherThrows = pitcherData?.throws || pitcher?.pitcherHand;

      const cellStyle = { padding: '3px 2px', textAlign: 'center', fontSize: fs, color: '#dee2e6' };

      const renderStatCells = (pitchStats, getData, clr) => {
        const c = clr || '#dee2e6';
        return pitchStats.map(({ pitch }) => {
          if (pitch.code.startsWith('empty_')) {
            return statCols.map((_, ci) => (
              <td key={`${pitch.code}_${ci}`} style={{ ...cellStyle, color: '#555' }}></td>
            ));
          }
          const d = getData(pitch.code);
          const s = computePitchStats(d);
          if (!s || s.noData) {
            return statCols.map((_, ci) => (
              <td key={`${pitch.code}_${ci}`} style={{ ...cellStyle, color: '#555' }}>-</td>
            ));
          }
          return isAdv ? [
            <td key={`${pitch.code}_n`} style={{ ...cellStyle, color: clr ? c : '#adb5bd', borderLeft: '2px solid rgb(26, 29, 33)' }}>{s.seen ?? '-'}</td>,
            <td key={`${pitch.code}_hh`} style={{ ...cellStyle }}>{renderBadge(s.hhRate, 0.45, 0.40)}</td>,
            <td key={`${pitch.code}_brl`} style={{ ...cellStyle }}>{renderBadge(s.brlRate, 0.15, 0.10)}</td>,
            <td key={`${pitch.code}_whf`} style={{ ...cellStyle, color: s.whiff >= 0.30 ? '#28a745' : c }}>{Math.round(s.whiff * 100)}%</td>,
            <td key={`${pitch.code}_chase`} style={{ ...cellStyle, color: c }}>{d?.chaseRate != null ? `${Math.round(d.chaseRate * 100)}%` : d?.chase != null && d?.pitchesOutsideZone ? `${Math.round((d.chase / d.pitchesOutsideZone) * 100)}%` : '-'}</td>,
          ] : [
            <td key={`${pitch.code}_n`} style={{ ...cellStyle, color: clr ? c : '#adb5bd', borderLeft: '2px solid rgb(26, 29, 33)' }}>{s.seen ?? '-'}</td>,
            <td key={`${pitch.code}_avg`} style={{ ...cellStyle }}>{renderDecimalBadge(s.avg, 0.350, 0.300, c)}</td>,
            <td key={`${pitch.code}_slg`} style={{ ...cellStyle }}>{renderDecimalBadge(s.slg, 0.550, 0.450, c)}</td>,
            <td key={`${pitch.code}_bip`} style={{ ...cellStyle, color: c }}>{s.bbe || '-'}</td>,
            <td key={`${pitch.code}_hr`} style={{ ...cellStyle, color: s.hr > 0 ? '#ffc107' : c }}>{s.hr}</td>,
          ];
        });
      };

      // Render pitcher row cells — when hand is provided, use byPitchHand data
      const renderPitcherCellsSummary = (pitchStats, hand) => {
        const byPitchHand = pitcherData?.seasonStats?.byPitchHand || {};
        return pitchStats.map(({ pitch, s: defaultS }) => {
          // For splits, look up hand-specific raw data then compute stats
          const handRaw = hand ? byPitchHand[`${pitch.code}_${hand}`] : null;
          const handStats = handRaw ? computePitchStats(handRaw) : null;
          const s = (handStats && !handStats.noData) ? handStats : hand ? null : defaultS;
          if (!s) return statCols.map((_, ci) => <td key={`p_${pitch.code}_${ci}`} style={{ ...cellStyle, color: '#555' }}>{pitch.code.startsWith('empty_') ? '' : '-'}</td>);
          const srcData = hand ? handRaw : pitch;
          return isAdv ? [
            <td key={`p_${pitch.code}_n`} style={{ ...cellStyle, color: '#ffc107', borderLeft: '2px solid rgb(26, 29, 33)' }}>{handRaw?.thrown ?? pitch.thrown ?? s.seen ?? '-'}</td>,
            <td key={`p_${pitch.code}_hh`} style={{ ...cellStyle }}>{renderBadge(s.hhRate, 0.45, 0.40)}</td>,
            <td key={`p_${pitch.code}_brl`} style={{ ...cellStyle }}>{renderBadge(s.brlRate, 0.15, 0.10)}</td>,
            <td key={`p_${pitch.code}_whf`} style={{ ...cellStyle, color: s.whiff >= 0.30 ? '#28a745' : '#ffc107' }}>{Math.round(s.whiff * 100)}%</td>,
            <td key={`p_${pitch.code}_chase`} style={{ ...cellStyle, color: '#ffc107' }}>{(handRaw?.chaseRate ?? pitch.chaseRate) != null ? `${Math.round((handRaw?.chaseRate ?? pitch.chaseRate) * 100)}%` : '-'}</td>,
          ] : [
            <td key={`p_${pitch.code}_n`} style={{ ...cellStyle, color: '#ffc107', borderLeft: '2px solid rgb(26, 29, 33)' }}>{handRaw?.thrown ?? pitch.thrown ?? s.seen ?? '-'}</td>,
            <td key={`p_${pitch.code}_avg`} style={{ ...cellStyle }}>{renderDecimalBadge(s.avg, 0.350, 0.300, '#ffc107')}</td>,
            <td key={`p_${pitch.code}_slg`} style={{ ...cellStyle }}>{renderDecimalBadge(s.slg, 0.550, 0.450, '#ffc107')}</td>,
            <td key={`p_${pitch.code}_bip`} style={{ ...cellStyle, color: '#ffc107' }}>{s.bbe || '-'}</td>,
            <td key={`p_${pitch.code}_hr`} style={{ ...cellStyle, color: s.hr > 0 ? '#dc3545' : '#ffc107' }}>{s.hr}</td>,
          ];
        });
      };

      const renderTable = (label, pitcherLabel, batterList, getDataFn, hand, overridePitchStats, overrideMaxPitchCols) => {
        const tablePitchStats = overridePitchStats || defaultPitchStats;
        const tableMaxPitchCols = overrideMaxPitchCols || maxPitchCols;
        const tableTotalCols = 1 + tableMaxPitchCols * numStats;
        return (
        <>
          {label && <div style={{ padding: '4px 12px', backgroundColor: '#2a2d31', borderBottom: '1px solid #333', fontSize: mobile ? '0.65rem' : '0.75rem', fontWeight: 600, color: '#ffc107' }}>{label}</div>}
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: tableMaxPitchCols * 200 + 120 }}>
            <colgroup>
              <col style={{ width: '15%' }} />
              {tablePitchStats.map(({ pitch }) => (
                statCols.map((_, ci) => (
                  <col key={`${pitch.code}_col_${ci}`} style={{ width: `${85 / (tableMaxPitchCols * numStats)}%` }} />
                ))
              ))}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: '1px solid #444' }}>
                <th style={{ padding: '4px 8px', fontSize: fs, color: '#adb5bd' }}></th>
                {tablePitchStats.map(({ pitch }) => (
                  <th key={pitch.code} colSpan={numStats} style={{ padding: '4px 2px', textAlign: 'center', fontSize: fs, color: '#ffc107', fontWeight: 600, borderLeft: '2px solid rgb(26, 29, 33)' }}>
                    {pitch.code.startsWith('empty_') ? '' : <>{pitchCodeName[pitch.code] || pitch.code} <span style={{ color: '#adb5bd', fontWeight: 400 }}>{pitch.usage}% {pitch.avgSpeed ? `${pitch.avgSpeed}mph` : ''}</span></>}
                  </th>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #333' }}>
                <th style={{ padding: '3px 8px', fontSize: mobile ? '0.6rem' : '0.65rem', color: '#adb5bd', textAlign: 'left' }}>{pitcherLabel || 'Batter'}</th>
                {tablePitchStats.map(({ pitch }) => (
                  statCols.map((col, ci) => (
                    <th key={`${pitch.code}_${ci}`} style={{ padding: '3px 2px', textAlign: 'center', fontSize: mobile ? '0.6rem' : '0.7rem', color: '#6c757d', borderLeft: ci === 0 ? '2px solid rgb(26, 29, 33)' : 'none' }}>{pitch.code.startsWith('empty_') ? '' : col}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              <tr
                style={{ borderBottom: '2px solid #ffc107', backgroundColor: 'rgba(255,193,7,0.08)', height: '38px' }}
                onContextMenu={pitcher ? handleBatterContextMenu(pitcher.id, pitcher.name, team?.team) : undefined}
              >
                <td style={{ padding: '3px 8px', color: '#ffc107', fontWeight: 600, fontSize: fs, whiteSpace: 'nowrap' }}>
                  {pitcher?.name || 'Pitcher'} <span style={{ color: '#adb5bd', fontSize: '0.6rem', fontWeight: 400 }}>({pitcher?.pitcherHand})</span>{pitcher && renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                </td>
                {renderPitcherCellsSummary(tablePitchStats, hand)}
              </tr>
              {batterList.map((m, i) => {
                const batterData = pitchesData.batterData?.[m.batterId];
                return (
                  <tr
                    key={m.batterId}
                    style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '38px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', cursor: 'pointer' }}
                    onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}
                  >
                    <td style={{ padding: '3px 8px', color: '#fff', fontSize: fs, whiteSpace: 'nowrap' }}>
                      {renderBatOrder(m.batterId, teamIdx)}{m.batterName} <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                    </td>
                    {renderStatCells(tablePitchStats, (code) => getDataFn(batterData, code))}
                  </tr>
                );
              })}
              {batterList.length === 0 && (
                <tr><td colSpan={tableTotalCols} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No batter data</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
        );
      };

      return (
        <div style={{ borderBottom: '1px solid #444' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <img src={getTeamLogoUrlForSport(opposingTeam?.team)} alt={opposingTeam?.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam?.team}</span>
            {pitcher && (
              <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
              </span>
            )}
            {bullpenDropdown}
          </div>
          {pitchesSplitMode === 'splits' ? (
            ['R', 'L'].map(hand => {
              const handBatters = allBatters.filter(m => {
                if (m.batterHand === 'S') return hand === (pitcherThrows === 'R' ? 'L' : 'R');
                return m.batterHand === hand;
              });
              // Hand-specific top pitches from byPitchHand so vs R / vs L show different pitch lists & usage %
              const handTopPitches = getTopPitchesForHand(pitcherData, hand);
              const handMaxPitchCols = Math.max(handTopPitches.length, 1);
              const handPitchStats = handTopPitches.map(p => ({ pitch: p, s: computePitchStats(p) }));
              while (handPitchStats.length < handMaxPitchCols) {
                handPitchStats.push({ pitch: { code: `empty_${handPitchStats.length}`, usage: 0 }, s: null });
              }
              return (
                <React.Fragment key={hand}>
                  {renderTable(
                    `${pitcher?.name || 'Pitcher'} vs ${hand === 'R' ? 'RHB' : 'LHB'}`,
                    'Batter',
                    handBatters,
                    (batterData, code) => batterData?.seasonStats?.byPitchHand?.[`${code}_${pitcherThrows}`] || batterData?.seasonStats?.byPitch?.[code],
                    hand,
                    handPitchStats,
                    handMaxPitchCols
                  )}
                </React.Fragment>
              );
            })
          ) : (
            renderTable(null, 'Batter', allBatters, (batterData, code) => batterData?.seasonStats?.byPitch?.[code])
          )}
        </div>
      );
    };

    const awayTeam = matchupsData?.away;
    const homeTeam = matchupsData?.home;

    return (
      <div style={{ overflow: 'auto', height: '100%' }}>
        {renderPitcherSection(pitchesData.homePitcher, homeTeam, awayTeam, 0)}
        {renderPitcherSection(pitchesData.awayPitcher, awayTeam, homeTeam, 1)}
      </div>
    );
  };

  // Last summary view — all periods as column groups, batters as rows, stacked by team
  const renderLastSummary = (mobile) => {
    if (!pitchesData || !matchupsData) return null;
    const fs = mobile ? '0.7rem' : '0.85rem';
    const periods = [
      { key: 'last7', label: 'L7' },
      { key: 'last14', label: 'L14' },
      { key: 'last30', label: 'L30' },
      { key: 'overall', label: 'Season' },
    ];
    const statCols = ['AVG', 'SLG', 'HH', 'Brl', 'HR', 'K', 'XBH'];
    const numStats = statCols.length;

    // Pitcher equivalent for batter-facing splits: vs LHP → vsLHB, vs RHP → vsRHB
    const getWindowStats = (data, period, isPitcher = false) => {
      if (!data) return null;
      const splitKey = isPitcher
        ? (lastSplit === 'vsLHP' ? 'vsLHB' : lastSplit === 'vsRHP' ? 'vsRHB' : 'overall')
        : lastSplit;
      const root = period === 'overall' ? data.seasonStats : data.windows?.[period];
      return root?.[splitKey] ?? root?.overall ?? null;
    };

    const renderPeriodCells = (data, style, isPitcher = false) => {
      return periods.map(p => {
        const s = getWindowStats(data, p.key, isPitcher);
        // Pitcher windows use `bf` (batters faced) rather than `pa`. Gate
        // on whichever count exists so pitcher rows aren't blanked out.
        const sampleSize = isPitcher ? (s?.bf || s?.pa) : s?.pa;
        if (!s || !sampleSize) {
          return statCols.map((_, ci) => (
            <td key={`${p.key}_${ci}`} style={{ ...style, color: '#555' }}>-</td>
          ));
        }
        const brl = s.barrelRate ?? (s.bbe > 0 ? (s.barrels || 0) / s.bbe : 0);
        const hh = s.hardHitRate ?? (s.bbe > 0 ? (s.hardHits || 0) / s.bbe : 0);
        const renderBrlBadge = (val) => {
          if (val >= 0.15) return <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(val * 100)}%</span>;
          if (val >= 0.10) return <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(val * 100)}%</span>;
          return <span style={{ color: style.color }}>{Math.round(val * 100)}%</span>;
        };
        const renderHhBadge = (val) => {
          if (val >= 0.45) return <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(val * 100)}%</span>;
          if (val >= 0.40) return <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(val * 100)}%</span>;
          return <span style={{ color: style.color }}>{Math.round(val * 100)}%</span>;
        };
        return [
          (() => {
            const avg = s.avg || 0;
            const text = avg.toFixed(3).replace(/^0\./, '.');
            const node = avg >= 0.350
              ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{text}</span>
              : avg >= 0.300
                ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{text}</span>
                : avg >= 0.275
                  ? <span style={{ color: '#F59E0B' }}>{text}</span>
                  : <span style={{ color: style.color }}>{text}</span>;
            return <td key={`${p.key}_avg`} style={{ ...style, borderLeft: '2px solid rgb(26, 29, 33)' }}>{node}</td>;
          })(),
          (() => {
            const slg = s.slg || 0;
            const text = slg.toFixed(3).replace(/^0\./, '.');
            const node = slg >= 0.550
              ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{text}</span>
              : slg >= 0.450
                ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{text}</span>
                : <span style={{ color: style.color }}>{text}</span>;
            return <td key={`${p.key}_slg`} style={{ ...style }}>{node}</td>;
          })(),
          <td key={`${p.key}_hh`} style={{ ...style }}>{hh != null ? renderHhBadge(hh) : '-'}</td>,
          <td key={`${p.key}_brl`} style={{ ...style }}>{brl != null ? renderBrlBadge(brl) : '-'}</td>,
          <td key={`${p.key}_hr`} style={{ ...style, color: s.hr > 0 ? '#ffc107' : style.color }}>{s.hr}</td>,
          <td key={`${p.key}_k`} style={{ ...style, color: style.color }}>{s.k}</td>,
          (() => {
            const xbh = s.xbh ?? ((s.doubles || 0) + (s.triples || 0) + (s.hr || 0));
            return <td key={`${p.key}_xbh`} style={{ ...style, color: xbh > 0 ? '#ffc107' : style.color }}>{xbh}</td>;
          })(),
        ];
      });
    };

    const renderTeamSection = (team, opposingTeam, teamIdx) => {
      const teamAbv = team?.team;
      const overridePitcher = teamAbv ? (bvpOverridePitchers[teamAbv] || null) : null;
      const pitcher = overridePitcher
        ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
        : team?.pitcher;
      const probablePitcherData = teamIdx === 0 ? pitchesData.homePitcher : pitchesData.awayPitcher;
      const opposingPitcherData = overridePitcher
        ? (bvpOverridePitcherData[teamAbv] || probablePitcherData)
        : probablePitcherData;
      const batters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || [])]
        .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));
      const isHomePitcher = teamAbv === visibleGame?.home;
      const bullpenList = buildBullpenList(visibleGame, isHomePitcher);

      const cellStyle = { padding: '3px 2px', textAlign: 'center', fontSize: fs, color: '#dee2e6' };
      const totalCols = 1 + periods.length * numStats;

      return (
        <div style={{ borderBottom: '1px solid #444' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <img src={getTeamLogoUrlForSport(opposingTeam?.team)} alt={opposingTeam?.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam?.team}</span>
            {pitcher && (
              <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
              </span>
            )}
            {teamAbv && (
              <BullpenDropdown
                bullpenList={bullpenList}
                selectedId={overridePitcher?.id}
                onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
                mobile={mobile}
              />
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: periods.length * 360 + 180 }}>
            <colgroup>
              <col style={{ width: '180px' }} />
              {periods.map(p => (
                statCols.map((_, ci) => (
                  <col key={`${p.key}_col_${ci}`} style={{ width: `${360 / numStats}px` }} />
                ))
              ))}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: '1px solid #444' }}>
                <th style={{ padding: '6px 8px', fontSize: fs, color: '#adb5bd' }}></th>
                {periods.map(p => (
                  <th key={p.key} colSpan={numStats} style={{ padding: '6px 4px', textAlign: 'center', fontSize: fs, color: '#ffc107', fontWeight: 600, borderLeft: '2px solid rgb(26, 29, 33)' }}>
                    {p.label}
                  </th>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #333', color: '#adb5bd' }}>
                <th style={{ padding: '6px 8px', fontSize: fs, color: '#adb5bd', textAlign: 'left' }}>Batter</th>
                {periods.map(p => (
                  statCols.map((col, ci) => (
                    <th key={`${p.key}_${ci}`} style={{ padding: '6px 4px', textAlign: 'center', fontSize: fs, borderLeft: ci === 0 ? '2px solid rgb(26, 29, 33)' : 'none' }}>{col}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Pitcher row */}
              {opposingPitcherData && (
                <tr style={{ borderBottom: '2px solid #ffc107', backgroundColor: 'rgba(255,193,7,0.08)', height: '38px' }}>
                  <td
                    style={{ padding: '3px 8px', color: '#ffc107', fontWeight: 600, fontSize: fs, whiteSpace: 'nowrap' }}
                    onContextMenu={pitcher ? handleBatterContextMenu(pitcher.id, pitcher.name, team?.team) : undefined}
                  >
                    {pitcher?.name || 'Pitcher'} <span style={{ color: '#adb5bd', fontSize: '0.6rem', fontWeight: 400 }}>({pitcher?.pitcherHand})</span>{pitcher && renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                  </td>
                  {renderPeriodCells(opposingPitcherData, { ...cellStyle, color: '#ffc107' }, true)}
                </tr>
              )}
              {/* Batter rows */}
              {batters.map((m, i) => {
                const bd = pitchesData.batterData?.[m.batterId];
                return (
                  <tr
                    key={m.batterId}
                    style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '38px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', cursor: 'pointer' }}
                    onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}
                  >
                    <td style={{ padding: '3px 8px', color: '#fff', fontSize: fs, whiteSpace: 'nowrap' }}>
                      {renderBatOrder(m.batterId, teamIdx)}{m.batterName} <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                    </td>
                    {renderPeriodCells(bd, cellStyle)}
                  </tr>
                );
              })}
              {batters.length === 0 && (
                <tr><td colSpan={totalCols} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No batter data</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      );
    };

    const awayTeam = matchupsData?.away;
    const homeTeam = matchupsData?.home;

    return (
      <div style={{ overflow: 'auto', height: '100%' }}>
        {renderTeamSection(homeTeam, awayTeam, 0)}
        {renderTeamSection(awayTeam, homeTeam, 1)}
      </div>
    );
  };

  const renderLastTable = (team, opposingTeam, teamIdx, mobile) => {
    if (!team || !pitchesData) return null;
    const teamAbv = team.team;
    const overridePitcher = bvpOverridePitchers[teamAbv] || null;
    const pitcher = overridePitcher
      ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
      : team.pitcher;
    const fontSize = mobile ? '0.7rem' : '0.85rem';
    const batters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || team.matchups || [])]
      .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));

    // Bullpen list — shared source, matches BvP / Pitches / All views.
    const isHomePitcher = team?.team === visibleGame?.home;
    const bullpenList = buildBullpenList(visibleGame, isHomePitcher);

    // Opposing pitcher's recent performance (home pitcher faces away batters and vice versa).
    // Prefer the reliever's fetched doc when a bullpen override is active.
    const probablePitcherData = teamIdx === 0 ? pitchesData.homePitcher : pitchesData.awayPitcher;
    const opposingPitcherData = overridePitcher
      ? (bvpOverridePitcherData[teamAbv] || probablePitcherData)
      : probablePitcherData;
    // Pitcher equivalent for batter-facing splits: vs LHP → vsLHB, vs RHP → vsRHB
    const pitcherSplit = lastSplit === 'vsLHP' ? 'vsLHB'
      : lastSplit === 'vsRHP' ? 'vsRHB'
      : 'overall';
    const getPitcherStats = () => {
      if (!opposingPitcherData) return null;
      const root = lastPeriod === 'overall' ? opposingPitcherData.seasonStats : opposingPitcherData.windows?.[lastPeriod];
      return root?.[pitcherSplit] ?? root?.overall ?? null;
    };
    const pitcherStats = getPitcherStats();

    const getStats = (batterId) => {
      const bd = pitchesData.batterData?.[batterId];
      if (!bd) return null;
      const root = lastPeriod === 'overall' ? bd.seasonStats : bd.windows?.[lastPeriod];
      return root?.[lastSplit] ?? root?.overall ?? null;
    };

    return (
      <div key={teamIdx} style={{ flex: mobile ? 'none' : 1, borderRight: !mobile && teamIdx === 0 ? '1px solid #333' : 'none', borderBottom: mobile && teamIdx === 0 ? '1px solid #444' : 'none', overflow: mobile ? 'visible' : 'auto' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', position: mobile ? 'static' : 'sticky', top: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam.team}</span>
            {pitcher && (
              <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
              </span>
            )}
            <BullpenDropdown
              bullpenList={bullpenList}
              selectedId={overridePitcher?.id}
              onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
              mobile={mobile}
            />
          </div>
        </div>
        <div style={{ overflowX: mobile ? 'auto' : 'visible' }}>
        <table style={{ width: '100%', fontSize, borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: mobile ? '800px' : 'auto' }}>
          <colgroup>
            <col style={{ width: '25%' }} />
            {Array.from({ length: 12 }).map((_, i) => (
              <col key={`last-detail-col-${i}`} style={{ width: `${75 / 12}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '1px solid #333', color: '#adb5bd' }}>
              <th style={{ padding: '6px 6px', textAlign: 'left', position: mobile ? 'static' : 'sticky', top: mobile ? 'auto' : 36, backgroundColor: '#212529', zIndex: 1 }}>Batter</th>
              {['AVG', 'SLG', 'HH', 'Brl', 'PA', 'AB', 'BBE', 'H', 'XBH', 'HR', 'K', 'BB'].map(col => (
                <th key={col} style={{ padding: '6px 2px', textAlign: 'center', position: mobile ? 'static' : 'sticky', top: mobile ? 'auto' : 36, backgroundColor: '#212529', zIndex: 1 }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pitcher && pitcherStats && (() => {
              const ps = pitcherStats;
              const noData = !ps || !ps.pa;
              return (
                <tr style={{ borderBottom: '2px solid #ffc107', backgroundColor: 'rgba(255,193,7,0.08)', height: '38px' }}>
                  <td
                    style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap', color: '#ffc107', fontWeight: 600 }}
                    onContextMenu={handleBatterContextMenu(pitcher.id, pitcher.name, team?.team)}
                  >
                    {pitcher.name} <span style={{ color: '#adb5bd', fontSize: '0.65rem', fontWeight: 400 }}>({pitcher.pitcherHand})</span>{renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                  </td>
                  {noData ? (
                    <td colSpan={12} style={{ padding: '6px 2px', textAlign: 'center', color: '#555' }}>-</td>
                  ) : (
                    <>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{ps.avg == null ? '-' : ps.avg >= 0.350 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{ps.avg.toFixed(3).replace(/^0\./, '.')}</span> : ps.avg >= 0.300 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{ps.avg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#ffc107' }}>{ps.avg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{ps.slg == null ? '-' : ps.slg >= 0.550 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{ps.slg.toFixed(3).replace(/^0\./, '.')}</span> : ps.slg >= 0.450 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{ps.slg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#ffc107' }}>{ps.slg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{ps.hardHitRate == null ? '-' : ps.hardHitRate >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(ps.hardHitRate * 100)}%</span> : ps.hardHitRate >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(ps.hardHitRate * 100)}%</span> : <span style={{ color: '#ffc107' }}>{Math.round(ps.hardHitRate * 100)}%</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{ps.barrelRate == null ? '-' : ps.barrelRate >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(ps.barrelRate * 100)}%</span> : ps.barrelRate >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(ps.barrelRate * 100)}%</span> : <span style={{ color: '#ffc107' }}>{Math.round(ps.barrelRate * 100)}%</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.pa}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.ab}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.bbe ?? '-'}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.h}</td>
                      {(() => {
                        const xbh = ps.xbh ?? ((ps.doubles || 0) + (ps.triples || 0) + (ps.hr || 0));
                        return <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{xbh}</td>;
                      })()}
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: ps.hr > 0 ? '#dc3545' : '#ffc107' }}>{ps.hr}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.k}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#ffc107' }}>{ps.bb}</td>
                    </>
                  )}
                </tr>
              );
            })()}
            {batters.map((m, i) => {
              const s = getStats(m.batterId);
              const noData = !s || !s.pa;
              return (
                <tr
                  key={i}
                  style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '38px', cursor: 'pointer' }}
                  onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}
                >
                  <td style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {renderBatOrder(m.batterId, teamIdx)}<span style={{ color: '#dee2e6' }}>{m.batterName}</span>
                    <span style={{ color: '#6c757d', fontSize: '0.7rem', marginLeft: '4px' }}>({m.batterHand})</span>{renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                  </td>
                  {noData ? (
                    <td colSpan={12} style={{ padding: '6px 2px', textAlign: 'center', color: '#555' }}>-</td>
                  ) : (
                    <>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.avg == null ? '-' : s.avg >= 0.350 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : s.avg >= 0.300 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : s.avg >= 0.275 ? <span style={{ color: '#F59E0B' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{s.avg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.slg == null ? '-' : s.slg >= 0.550 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : s.slg >= 0.450 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span> : <span style={{ color: '#dee2e6' }}>{s.slg.toFixed(3).replace(/^0\./, '.')}</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.hardHitRate == null ? '-' : s.hardHitRate >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(s.hardHitRate * 100)}%</span> : s.hardHitRate >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(s.hardHitRate * 100)}%</span> : <span style={{ color: '#dee2e6' }}>{Math.round(s.hardHitRate * 100)}%</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center' }}>{s.barrelRate == null ? '-' : s.barrelRate >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(s.barrelRate * 100)}%</span> : s.barrelRate >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' }}>{Math.round(s.barrelRate * 100)}%</span> : <span style={{ color: '#dee2e6' }}>{Math.round(s.barrelRate * 100)}%</span>}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.pa}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.ab}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.bbe ?? '-'}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.h}</td>
                      {(() => {
                        const xbh = s.xbh ?? ((s.doubles || 0) + (s.triples || 0) + (s.hr || 0));
                        return <td style={{ padding: '6px 2px', textAlign: 'center', color: xbh > 0 ? '#ffc107' : '#dee2e6' }}>{xbh}</td>;
                      })()}
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: s.hr > 0 ? '#ffc107' : '#dee2e6' }}>{s.hr}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.k}</td>
                      <td style={{ padding: '6px 2px', textAlign: 'center', color: '#dee2e6' }}>{s.bb}</td>
                    </>
                  )}
                </tr>
              );
            })}
            {batters.length === 0 && (
              <tr><td colSpan={11} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No batter data</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  // ALL view — per-batter row with three side-by-side summaries:
  // BvP (signal bars + AVG/SLG/HH/Brl), Pitches (cumulative across all of
  // the pitcher's pitches: BBE/AVG/SLG/HH/Brl), Last (BBE/AVG/SLG/HH/Brl
  // pulled from the batter's overall season stats).
  const renderAllView = (mobile) => {
    if (!matchupsData || !pitchesData) return null;
    // Match the body/header sizes used by BvP / Pitches / Last so all four
    // views feel consistent.
    const fs = mobile ? '0.7rem' : '0.85rem';
    const headerFs = mobile ? '0.6rem' : '0.7rem';

    // Inline badge renderers — same thresholds AND shape as BvP / Last views:
    // 2px 5px padding, 4px radius, bold, inline-block, fixed minWidth, centered.
    const fmtAvg = (v) => v.toFixed(3).replace(/^0\./, '.');
    const slashBadge = { padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '40px', textAlign: 'center' };
    const rateBadge = { padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold', display: 'inline-block', minWidth: '36px', textAlign: 'center' };
    const renderAvg = (v) => {
      if (v == null) return '-';
      if (v >= 0.350) return <span style={{ ...slashBadge, backgroundColor: 'green', color: 'yellow' }}>{fmtAvg(v)}</span>;
      if (v >= 0.300) return <span style={{ ...slashBadge, backgroundColor: '#F59E0B', color: '#1E40AF' }}>{fmtAvg(v)}</span>;
      if (v >= 0.275) return <span style={{ color: '#F59E0B' }}>{fmtAvg(v)}</span>;
      return <span style={{ color: '#dee2e6' }}>{fmtAvg(v)}</span>;
    };
    const renderSlg = (v) => {
      if (v == null) return '-';
      if (v >= 0.550) return <span style={{ ...slashBadge, backgroundColor: 'green', color: 'yellow' }}>{fmtAvg(v)}</span>;
      if (v >= 0.450) return <span style={{ ...slashBadge, backgroundColor: '#F59E0B', color: '#1E40AF' }}>{fmtAvg(v)}</span>;
      return <span style={{ color: '#dee2e6' }}>{fmtAvg(v)}</span>;
    };
    const renderHh = (v) => {
      if (v == null) return '-';
      const pct = `${Math.round(v * 100)}%`;
      if (v >= 0.45) return <span style={{ ...rateBadge, backgroundColor: 'green', color: 'yellow' }}>{pct}</span>;
      if (v >= 0.40) return <span style={{ ...rateBadge, backgroundColor: '#F59E0B', color: '#1E40AF' }}>{pct}</span>;
      return <span style={{ color: '#dee2e6' }}>{pct}</span>;
    };
    const renderBrl = (v) => {
      if (v == null) return '-';
      const pct = `${Math.round(v * 100)}%`;
      if (v >= 0.15) return <span style={{ ...rateBadge, backgroundColor: 'green', color: 'yellow' }}>{pct}</span>;
      if (v >= 0.10) return <span style={{ ...rateBadge, backgroundColor: '#F59E0B', color: '#1E40AF' }}>{pct}</span>;
      return <span style={{ color: '#dee2e6' }}>{pct}</span>;
    };
    const renderBars = (pa) => {
      if (!pa) return '';
      const BAR_THRESHOLDS = [1, 3, 6, 8, 10];
      const lit = BAR_THRESHOLDS.filter(t => pa >= t).length;
      return (
        <svg width="26" height="14" viewBox="0 0 26 20" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          {[1, 2, 3, 4, 5].map(bar => {
            const h = 4 + (bar - 1) * 3.5;
            const x = 1.5 + (bar - 1) * 5;
            const y = 18 - h;
            return <rect key={bar} x={x} y={y} width="3.5" height={h} rx="0.75" fill={bar <= lit ? '#9CA3AF' : '#1f2937'} />;
          })}
        </svg>
      );
    };

    // Sum the batter's stats against every pitch the pitcher throws. When
    // `splits` is true the lookup uses byPitchHand[`${code}_${pitcherThrows}`]
    // so the result is filtered to the pitcher's handedness.
    const cumulativePitchStats = (batterData, pitcherData, splits) => {
      if (!batterData) return null;
      const pitcherByPitch = pitcherData?.seasonStats?.byPitch || {};
      const batterByPitch = batterData.seasonStats?.byPitch || {};
      const batterByPitchHand = batterData.seasonStats?.byPitchHand || {};
      const hand = pitcherData?.throws || null;
      let h = 0, ab = 0, bbe = 0, doubles = 0, triples = 0, hr = 0, hardHits = 0, barrels = 0;
      let found = false;
      Object.keys(pitcherByPitch).forEach(code => {
        const d = (splits && hand)
          ? batterByPitchHand[`${code}_${hand}`]
          : batterByPitch[code];
        if (!d) return;
        found = true;
        h += d.h || 0;
        // AB only — never fall back to BBE. AVG / SLG should be computed
        // against true at-bats so K's / non-BBE outs count in the denominator.
        ab += d.ab || 0;
        bbe += d.bbe || 0;
        doubles += d.doubles || d['2b'] || 0;
        triples += d.triples || d['3b'] || 0;
        hr += d.hr || 0;
        hardHits += d.hardHits || 0;
        barrels += d.barrels || 0;
      });
      if (!found || (ab === 0 && bbe === 0)) {
        const overall = batterData.seasonStats?.overall;
        if (!overall) return null;
        return {
          bbe: overall.bbe ?? 0,
          avg: overall.avg ?? null,
          slg: overall.slg ?? null,
          hhRate: overall.hardHitRate ?? null,
          brlRate: overall.barrelRate ?? null,
          hr: overall.hr ?? 0,
        };
      }
      const singles = h - doubles - triples - hr;
      const avg = ab > 0 ? h / ab : null;
      const slg = ab > 0 ? (singles + doubles * 2 + triples * 3 + hr * 4) / ab : null;
      const hhRate = bbe > 0 ? hardHits / bbe : null;
      const brlRate = bbe > 0 ? barrels / bbe : null;
      return { bbe, avg, slg, hhRate, brlRate, hr };
    };

    // All view always uses Last 7 regardless of the Last tab's lastPeriod state.
    // When `splits` is true the split key is derived from the pitcher's hand:
    // vsLHP for L pitcher, vsRHP for R pitcher.
    const getLastStats = (batterData, pitcherHand) => {
      if (!batterData) return null;
      const splitKey = (allSplitsLast && pitcherHand)
        ? (pitcherHand === 'L' ? 'vsLHP' : 'vsRHP')
        : lastSplit;
      const root = batterData.windows?.last7;
      const stats = root?.[splitKey] ?? root?.overall ?? null;
      if (!stats) return null;
      return {
        bbe: stats.bbe ?? 0,
        avg: stats.avg ?? null,
        slg: stats.slg ?? null,
        hhRate: stats.hardHitRate ?? null,
        brlRate: stats.barrelRate ?? null,
        hr: stats.hr ?? 0,
      };
    };

    // Header styling matched to the Pitches view: gold section headers in
    // sans-serif (no Oswald / uppercase / letter-spacing), grey stat sub-
    // headers, and a `rgb(26,29,33)` border-left to mark each section.
    const sectionStyle = { padding: '4px 2px', textAlign: 'center', fontSize: fs, color: '#ffc107', fontWeight: 600 };
    const colStyle = { padding: '3px 2px', textAlign: 'center', fontSize: headerFs, color: '#6c757d' };
    const cellStyle = { padding: '4px 4px', textAlign: 'center', fontSize: fs };
    const sectionBorder = '2px solid rgb(26, 29, 33)';

    const renderTeamTable = (team, opposingTeam, teamIdx) => {
      if (!team) return null;
      const teamAbv = team.team;
      const overridePitcher = bvpOverridePitchers[teamAbv] || null;
      // Header/pitcher and BvP fetches use the reliever when a bullpen
      // override is active for this team; Pitches/Last still key off the
      // probable pitcher's stats (`opposingPitcherData`) since we don't
      // fetch per-reliever pitch data here.
      const pitcher = overridePitcher
        ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
        : team.pitcher;
      // Prefer the reliever's fetched pitcher doc for the Pitches columns so
      // the arsenal (byPitch / byPitchHand) matches the selected reliever.
      const probablePitcherData = teamIdx === 0 ? pitchesData.homePitcher : pitchesData.awayPitcher;
      const opposingPitcherData = overridePitcher
        ? (bvpOverridePitcherData[teamAbv] || probablePitcherData)
        : probablePitcherData;
      const batters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || [])]
        .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));
      // Look up BvP rows from matchupsData so we can pair with batter ids.
      const bvpRows = team.matchups || [];
      const bvpByBatterId = {};
      bvpRows.forEach(m => {
        const id = String(m.batterId).replace(/^mlb_/, '');
        bvpByBatterId[id] = m;
      });

      // Bullpen list — same shared helper as the BvP view.
      const isHomePitcher = team?.team === visibleGame?.home;
      const bullpenList = buildBullpenList(visibleGame, isHomePitcher);

      // Pitcher season line — IP + rate stats appended to the header. Fields
      // fall back across the known name variants so this works whether the
      // backend keys season stats as h/k/hr or hits/strikeouts/homeRuns.
      // Splits are keyed vsLHB / vsRHB (batter hand from pitcher's POV).
      const buildPitcherLineStats = (statsBlock) => {
        if (!statsBlock) return null;
        const rawIp = statsBlock.ip ?? statsBlock.inningsPitched;
        const ip = rawIp != null ? Number(rawIp) : null;
        if (ip == null || Number.isNaN(ip)) return null;
        const ipDisplay = (() => {
          const full = Math.floor(ip);
          const dec = ip - full;
          if (dec < 0.1) return `${full}.0`;
          if (dec < 0.5) return `${full}.1`;
          return `${full}.2`;
        })();
        const per9 = (n) => (n != null && ip > 0 ? (n * 9) / ip : null);
        const h9v = per9(statsBlock.h ?? statsBlock.hits);
        const k9v = per9(statsBlock.k ?? statsBlock.strikeouts);
        const hr9v = per9(statsBlock.hr ?? statsBlock.homeRuns);
        const num = (v, d) => (v != null ? v.toFixed(d) : '-');
        return {
          ip: ipDisplay,
          h9: num(h9v, 1),
          k9: num(k9v, 1),
          hr9: num(hr9v, 2),
          k9Hot: k9v != null && k9v >= 9,
          h9Hot: h9v != null && h9v >= 9,
          hr9Hot: hr9v != null && hr9v >= 1.5,
        };
      };
      const pitcherLineStats = buildPitcherLineStats(opposingPitcherData?.seasonStats?.overall);
      const pitcherLineStatsVsL = buildPitcherLineStats(opposingPitcherData?.seasonStats?.vsLHB);
      const pitcherLineStatsVsR = buildPitcherLineStats(opposingPitcherData?.seasonStats?.vsRHB);
      const renderPitcherStatChunk = (s) => (
        <>
          <span style={{ color: '#adb5bd', marginRight: '3px' }}>IP</span><b>{s.ip}</b>
          <span style={{ color: '#adb5bd', margin: '0 3px 0 10px' }}>H/9</span><b style={{ color: s.h9Hot ? '#dc3545' : '#dee2e6' }}>{s.h9}</b>
          <span style={{ color: '#adb5bd', margin: '0 3px 0 10px' }}>K/9</span><b style={{ color: s.k9Hot ? '#28a745' : '#dee2e6' }}>{s.k9}</b>
          <span style={{ color: '#adb5bd', margin: '0 3px 0 10px' }}>HR/9</span><b style={{ color: s.hr9Hot ? '#ffc107' : '#dee2e6' }}>{s.hr9}</b>
        </>
      );

      return (
        <div key={teamIdx} style={{ flex: 'none', borderBottom: teamIdx === 0 ? '1px solid #444' : 'none', overflow: 'visible' }}>
          {/* Team / pitcher header — same shape as the other views */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
              <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam.team}</span>
              {pitcher && (
                <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                  vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                </span>
              )}
              <BullpenDropdown
                bullpenList={bullpenList}
                selectedId={overridePitcher?.id}
                onSelect={(reliever) => handleBullpenSelect(teamAbv, reliever)}
                mobile={mobile}
              />
              {(pitcherLineStats || pitcherLineStatsVsL || pitcherLineStatsVsR) && (
                // Stacked stat block to the right of the bullpen dropdown so
                // the header stays uncluttered — overall on top, splits below.
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', color: '#dee2e6', fontSize: mobile ? '0.7rem' : '0.75rem', fontWeight: 400, lineHeight: 1.2 }}>
                  {pitcherLineStats && (
                    <span>
                      <span style={{ color: '#ffc107', fontWeight: 600, marginRight: '6px' }}>Overall</span>
                      {renderPitcherStatChunk(pitcherLineStats)}
                    </span>
                  )}
                  {pitcherLineStatsVsL && (
                    <span>
                      <span style={{ color: '#ffc107', fontWeight: 600, marginRight: '6px' }}>vs LHB</span>
                      {renderPitcherStatChunk(pitcherLineStatsVsL)}
                    </span>
                  )}
                  {pitcherLineStatsVsR && (
                    <span>
                      <span style={{ color: '#ffc107', fontWeight: 600, marginRight: '6px' }}>vs RHB</span>
                      {renderPitcherStatChunk(pitcherLineStatsVsR)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ overflowX: mobile ? 'auto' : 'visible' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: mobile ? '1160px' : 'auto' }}>
              {/* Fixed column widths so both team tables render identical
                  column sizes. BvP stat cols are slightly narrower than the
                  Pitches/Last cols since they hold the same badge widths. */}
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '4%' }} />
                {Array.from({ length: 6 }).map((_, i) => (
                  <col key={`bvp-col-${i}`} style={{ width: '3.7%' }} />
                ))}
                {Array.from({ length: 12 }).map((_, i) => (
                  <col key={`pl-col-${i}`} style={{ width: `${55.8 / 12}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr style={{ borderBottom: '1px solid #444' }}>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }} rowSpan={2}>
                    <span style={{ color: '#adb5bd', fontSize: headerFs }}>Batter</span>
                  </th>
                  <th colSpan={7} style={{ ...sectionStyle, borderLeft: sectionBorder }}>BvP</th>
                  <th colSpan={6} style={{ ...sectionStyle, borderLeft: sectionBorder }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      Pitches
                      <button
                        type="button"
                        onClick={() => setAllSplitsPitches(v => !v)}
                        title={`vs. Pitcher Hand ${allSplitsPitches ? 'on' : 'off'}`}
                        style={{
                          background: allSplitsPitches ? '#ffc107' : 'transparent',
                          border: `1px solid ${allSplitsPitches ? '#ffc107' : '#6c757d'}`,
                          color: allSplitsPitches ? '#000' : '#adb5bd',
                          fontSize: '0.6rem',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        vs. Pitcher Hand
                      </button>
                    </span>
                  </th>
                  <th colSpan={6} style={{ ...sectionStyle, borderLeft: sectionBorder }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      Last 7
                      <button
                        type="button"
                        onClick={() => setAllSplitsLast(v => !v)}
                        title={`vs. Pitcher Hand ${allSplitsLast ? 'on' : 'off'}`}
                        style={{
                          background: allSplitsLast ? '#ffc107' : 'transparent',
                          border: `1px solid ${allSplitsLast ? '#ffc107' : '#6c757d'}`,
                          color: allSplitsLast ? '#000' : '#adb5bd',
                          fontSize: '0.6rem',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        vs. Pitcher Hand
                      </button>
                    </span>
                  </th>
                </tr>
                <tr style={{ borderBottom: '1px solid #333' }}>
                  <th style={{ ...colStyle, borderLeft: sectionBorder, width: '28px' }}></th>
                  <th style={colStyle}>BBE</th>
                  <th style={colStyle}>AVG</th>
                  <th style={colStyle}>SLG</th>
                  <th style={colStyle}>HH%</th>
                  <th style={colStyle}>Brl%</th>
                  <th style={colStyle}>HR</th>
                  <th style={{ ...colStyle, borderLeft: sectionBorder }}>BBE</th>
                  <th style={colStyle}>AVG</th>
                  <th style={colStyle}>SLG</th>
                  <th style={colStyle}>HH%</th>
                  <th style={colStyle}>Brl%</th>
                  <th style={colStyle}>HR</th>
                  <th style={{ ...colStyle, borderLeft: sectionBorder }}>BBE</th>
                  <th style={colStyle}>AVG</th>
                  <th style={colStyle}>SLG</th>
                  <th style={colStyle}>HH%</th>
                  <th style={colStyle}>Brl%</th>
                  <th style={colStyle}>HR</th>
                </tr>
              </thead>
              <tbody>
                {batters.map((m, i) => {
                  const bd = pitchesData.batterData?.[m.batterId];
                  const bvpMatch = bvpByBatterId[String(m.batterId).replace(/^mlb_/, '')];
                  const bvp = bvpMatch ? getBvpStats(bvpMatch, teamAbv) : null;
                  const pitchCum = cumulativePitchStats(bd, opposingPitcherData, allSplitsPitches);
                  const lastSt = getLastStats(bd, opposingPitcherData?.throws || pitcher?.pitcherHand);
                  const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)';
                  return (
                    <tr
                      key={m.batterId}
                      style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '34px', backgroundColor: rowBg, cursor: 'pointer' }}
                      onContextMenu={handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team)}
                    >
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', color: '#fff', fontSize: fs }}>
                        {renderBatOrder(m.batterId, teamIdx)}{m.batterName}{' '}
                        <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>({m.batterHand})</span>
                        {renderPlayerIcon(m.batterId, m.batterName, opposingTeam?.team)}
                      </td>
                      {/* BvP */}
                      <td
                        style={{ ...cellStyle, borderLeft: sectionBorder }}
                        title={bvp?.pa ? `${bvp.pa} PA vs this pitcher` : 'No history vs this pitcher'}
                      >
                        {bvp?.pa ? renderBars(bvp.pa) : <span style={{ color: '#555' }}>-</span>}
                      </td>
                      <td style={{ ...cellStyle, color: '#dee2e6' }}>{bvp?.pa ? (bvp?.bbe ?? '-') : '-'}</td>
                      <td style={cellStyle}>{bvp?.pa ? renderAvg(bvp.avg ?? 0) : '-'}</td>
                      <td style={cellStyle}>{bvp?.pa ? renderSlg(bvp.slg ?? 0) : '-'}</td>
                      <td style={cellStyle}>{bvp?.pa ? renderHh(bvp.hardHitRate ?? 0) : '-'}</td>
                      <td style={cellStyle}>{bvp?.pa ? renderBrl(bvp.barrelRate ?? 0) : '-'}</td>
                      <td style={{ ...cellStyle, color: bvp?.hr > 0 ? '#ffc107' : '#dee2e6' }}>{bvp?.pa ? (bvp?.hr ?? 0) : '-'}</td>
                      {/* Pitches cumulative */}
                      <td style={{ ...cellStyle, borderLeft: sectionBorder, color: '#dee2e6' }}>{pitchCum?.bbe ?? '-'}</td>
                      <td style={cellStyle}>{pitchCum ? renderAvg(pitchCum.avg) : '-'}</td>
                      <td style={cellStyle}>{pitchCum ? renderSlg(pitchCum.slg) : '-'}</td>
                      <td style={cellStyle}>{pitchCum ? renderHh(pitchCum.hhRate) : '-'}</td>
                      <td style={cellStyle}>{pitchCum ? renderBrl(pitchCum.brlRate) : '-'}</td>
                      <td style={{ ...cellStyle, color: pitchCum?.hr > 0 ? '#ffc107' : '#dee2e6' }}>{pitchCum ? (pitchCum.hr ?? 0) : '-'}</td>
                      {/* Last overall */}
                      <td style={{ ...cellStyle, borderLeft: sectionBorder, color: '#dee2e6' }}>{lastSt?.bbe ?? '-'}</td>
                      <td style={cellStyle}>{lastSt ? renderAvg(lastSt.avg) : '-'}</td>
                      <td style={cellStyle}>{lastSt ? renderSlg(lastSt.slg) : '-'}</td>
                      <td style={cellStyle}>{lastSt ? renderHh(lastSt.hhRate) : '-'}</td>
                      <td style={cellStyle}>{lastSt ? renderBrl(lastSt.brlRate) : '-'}</td>
                      <td style={{ ...cellStyle, color: lastSt?.hr > 0 ? '#ffc107' : '#dee2e6' }}>{lastSt ? (lastSt.hr ?? 0) : '-'}</td>
                    </tr>
                  );
                })}
                {batters.length === 0 && (
                  <tr><td colSpan={19} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const awayTeam = matchupsData?.away;
    const homeTeam = matchupsData?.home;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
        {renderTeamTable(homeTeam, awayTeam, 0)}
        {renderTeamTable(awayTeam, homeTeam, 1)}
      </div>
    );
  };

  // K view — whiff-rate zone heat maps. Each team pane is split into two
  // sub-columns: vs LHB and vs RHB. The pitcher heat map at the top of each
  // sub-column shows their induced whiff rate against that batter hand;
  // below it, the batters of that hand list, each contextualised to
  // "vs this pitcher's throwing hand" so the cards reflect the matchup.
  //
  // byZoneHand keys are "<zone>_<hand>". For a BATTER, the hand suffix
  // encodes the opposing pitcher's throwing hand ("_R" = vs RHP). For a
  // PITCHER, the suffix encodes the opposing batter's hand ("_L" = vs LHB).
  const filterByZoneByHand = (seasonStats, handSuffix) => {
    if (!seasonStats) return null;
    if (!handSuffix) return seasonStats.byZone || null;
    const src = seasonStats.byZoneHand;
    if (!src) return null;
    const out = {};
    const suffix = `_${handSuffix}`;
    Object.entries(src).forEach(([k, v]) => {
      if (k.endsWith(suffix)) out[k.slice(0, -suffix.length)] = v;
    });
    return out;
  };
  // Sum per-zone swings/whiffs (and a few tag-along counts) across every
  // batter's byZone map so the result plugs directly into WhiffZoneHeatmap.
  // Returns null when nothing contributed, so the "No swing data" fallback
  // still fires.
  const composeBatterZones = (batterList, batterDataMap) => {
    const acc = {};
    let hasAny = false;
    batterList.forEach((m) => {
      const src = batterDataMap?.[m.batterId]?.seasonStats?.byZone;
      if (!src) return;
      Object.entries(src).forEach(([zoneKey, z]) => {
        if (!acc[zoneKey]) acc[zoneKey] = { swings: 0, whiffs: 0, seen: 0 };
        acc[zoneKey].swings += z.swings || 0;
        acc[zoneKey].whiffs += z.whiffs || 0;
        acc[zoneKey].seen += z.seen || z.thrown || 0;
        if (acc[zoneKey].swings > 0) hasAny = true;
      });
    });
    return hasAny ? acc : null;
  };
  const renderKView = (mobile) => {
    if (!matchupsData || !pitchesData) return null;
    const heatSize = mobile ? 150 : 220;
    const heatH = Math.round(heatSize * 1.28);
    const renderHeatCard = ({ key, badge, name, sub, byZone, borderColor, onContextMenu }) => (
      <div
        key={key}
        onContextMenu={onContextMenu}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${borderColor || '#2a2d31'}`, borderRadius: '4px', padding: '6px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', fontSize: mobile ? '0.7rem' : '0.75rem', color: '#dee2e6', textAlign: 'center' }}>
          {badge}
          <span style={{ fontWeight: 600 }}>{name}</span>
          {sub && <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>{sub}</span>}
        </div>
        {byZone
          ? <WhiffZoneHeatmap byZone={byZone} size={heatSize} mode={kHeatmapMode} whiffDenom={kWhiffDenom} />
          : <div style={{ width: heatSize, height: heatH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '0.7rem' }}>No swing data</div>}
      </div>
    );
    const renderTeamSection = (team, opposingTeam, teamIdx) => {
      if (!team) return null;
      const overridePitcher = bvpOverridePitchers[team.team] || null;
      const pitcher = overridePitcher
        ? { id: overridePitcher.id, name: overridePitcher.name, pitcherHand: overridePitcher.hand }
        : team.pitcher;
      const isHomePitcher = team?.team === visibleGame?.home;
      const bullpenList = buildBullpenList(visibleGame, isHomePitcher);
      const batters = [...((teamIdx === 0 ? pitchesData.awayBatters : pitchesData.homeBatters) || [])]
        .sort((a, b) => getBattingOrder(a.batterId, teamIdx) - getBattingOrder(b.batterId, teamIdx));
      // Pitcher data for this side — teamIdx 0 owns the home pitcher, teamIdx
      // 1 owns the away pitcher. If the user selected a reliever, prefer the
      // reliever's fetched pitcher doc (bvpOverridePitcherData) so the zone
      // shown matches the selected arm.
      const probablePitcherData = teamIdx === 0 ? pitchesData.homePitcher : pitchesData.awayPitcher;
      const pitcherData = overridePitcher
        ? (bvpOverridePitcherData[team.team] || probablePitcherData)
        : probablePitcherData;
      const pitcherSeasonStats = pitcherData?.seasonStats;
      const pitcherByZoneVsL = filterByZoneByHand(pitcherSeasonStats, 'L');
      const pitcherByZoneVsR = filterByZoneByHand(pitcherSeasonStats, 'R');
      // Split batters by handedness. Switch hitters (S) bat opposite the
      // pitcher's throwing hand — vs RHP they bat L, vs LHP they bat R —
      // so they slot into the column that matches their swing side today.
      const pitcherHand = pitcher?.pitcherHand;
      const lhbBatters = batters.filter(m => m.batterHand === 'L' || (m.batterHand === 'S' && pitcherHand === 'R'));
      const rhbBatters = batters.filter(m => m.batterHand === 'R' || (m.batterHand === 'S' && pitcherHand === 'L'));
      // Batter cards use the batter's "vs <pitcher hand>" split so the shown
      // whiff pattern reflects the matchup. Falls through to null (rendering
      // "No swing data") if the split isn't in the payload.
      const batterZone = (bd) => filterByZoneByHand(bd?.seasonStats, pitcherHand || null);
      const renderColumn = (side, columnBatters, pitcherZone) => (
        <div style={{
          flex: 1,
          padding: '10px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'center',
          borderRight: side === 'L' ? '1px solid #2a2d31' : 'none',
          minWidth: 0,
        }}>
          <div style={{ color: '#ffc107', fontSize: mobile ? '0.7rem' : '0.75rem', fontWeight: 700, letterSpacing: '0.5px' }}>
            vs {side}HB
          </div>
          {pitcher && renderHeatCard({
            key: `p_vs${side}_${pitcher.id || teamIdx}`,
            badge: <span style={{ color: '#ffc107', fontSize: '0.6rem', fontWeight: 700, marginRight: '2px' }}>P</span>,
            name: pitcher.name || 'Pitcher',
            sub: pitcher.pitcherHand ? `(${pitcher.pitcherHand}HP)` : null,
            byZone: pitcherZone,
            borderColor: '#ffc10755',
          })}
          {columnBatters.map((m) => renderHeatCard({
            key: m.batterId,
            badge: renderBatOrder(m.batterId, teamIdx),
            name: m.batterName,
            sub: `(${m.batterHand})`,
            byZone: batterZone(pitchesData.batterData?.[m.batterId]),
            onContextMenu: handleBatterContextMenu(m.batterId, m.batterName, opposingTeam?.team),
          }))}
          {columnBatters.length === 0 && (
            <div style={{ color: '#555', fontSize: '0.7rem', padding: '4px 0' }}>No {side}HB</div>
          )}
        </div>
      );
      return (
        <div key={teamIdx} style={{ flex: 1, overflow: 'auto', borderRight: teamIdx === 0 ? '1px solid #333' : 'none' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: mobile ? 20 : 24, height: mobile ? 20 : 24 }} />
              <span style={{ color: '#fff', fontWeight: '600', fontSize: mobile ? '0.85rem' : '0.95rem' }}>{opposingTeam.team}</span>
              {pitcher && (
                <span style={{ color: '#ffc107', fontSize: mobile ? '0.75rem' : '0.85rem' }}>
                  vs {team?.team && <img src={getTeamLogoUrlForSport(team.team)} alt={team.team} style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 4px 0 2px' }} />}{pitcher.name} ({pitcher.pitcherHand}){renderPlayerIcon(pitcher.id, pitcher.name, team?.team)}
                </span>
              )}
              <BullpenDropdown
                bullpenList={bullpenList}
                selectedId={overridePitcher?.id}
                onSelect={(reliever) => handleBullpenSelect(team.team, reliever)}
                mobile={mobile}
              />
            </div>
          </div>
          {kLayoutMode === 'composite' ? (() => {
            // Composite: one team-level aggregate + opposing pitcher overall,
            // shown side-by-side with larger cards. Pitcher shows overall
            // byZone (unsplit) since the batter aggregate spans all hands.
            const compositeByZone = composeBatterZones(batters, pitchesData.batterData);
            const pitcherOverall = pitcherSeasonStats?.byZone;
            const bigSize = mobile ? 180 : 240;
            return (
              <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', padding: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {pitcher && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{ color: '#ffc107', fontSize: mobile ? '0.7rem' : '0.75rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                      Pitcher
                    </div>
                    <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid #ffc10755', borderRadius: '4px', padding: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px', fontSize: mobile ? '0.75rem' : '0.8rem', color: '#dee2e6' }}>
                        <span style={{ color: '#ffc107', fontSize: '0.65rem', fontWeight: 700 }}>P</span>
                        <span style={{ fontWeight: 600 }}>{pitcher.name || 'Pitcher'}</span>
                        {pitcher.pitcherHand && <span style={{ color: '#6c757d', fontSize: '0.65rem' }}>({pitcher.pitcherHand}HP)</span>}
                      </div>
                      {pitcherOverall
                        ? <WhiffZoneHeatmap byZone={pitcherOverall} size={bigSize} mode={kHeatmapMode} whiffDenom={kWhiffDenom} />
                        : <div style={{ width: bigSize, height: Math.round(bigSize * 1.28), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>No swing data</div>}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <div style={{ color: '#ffc107', fontSize: mobile ? '0.7rem' : '0.75rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                    Lineup composite
                  </div>
                  <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid #2a2d31', borderRadius: '4px', padding: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px', fontSize: mobile ? '0.75rem' : '0.8rem', color: '#dee2e6' }}>
                      <img src={getTeamLogoUrlForSport(opposingTeam.team)} alt={opposingTeam.team} style={{ width: '16px', height: '16px' }} />
                      <span style={{ fontWeight: 600 }}>{opposingTeam.team}</span>
                      <span style={{ color: '#6c757d', fontSize: '0.65rem' }}>{batters.length} batters</span>
                    </div>
                    {compositeByZone
                      ? <WhiffZoneHeatmap byZone={compositeByZone} size={bigSize} mode={kHeatmapMode} whiffDenom={kWhiffDenom} />
                      : <div style={{ width: bigSize, height: Math.round(bigSize * 1.28), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>No swing data</div>}
                  </div>
                </div>
              </div>
            );
          })() : (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {renderColumn('L', lhbBatters, pitcherByZoneVsL)}
              {renderColumn('R', rhbBatters, pitcherByZoneVsR)}
            </div>
          )}
        </div>
      );
    };
    return (
      <div style={{ display: 'flex', gap: '0', height: '100%', overflow: 'hidden' }}>
        {renderTeamSection(homeTeam, awayTeam, 0)}
        {renderTeamSection(awayTeam, homeTeam, 1)}
      </div>
    );
  };

  const matchupsContent = (
    <>
      {matchupsView === 'bvp' && matchupsLoading && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-warning" role="status" />
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>Loading matchups...</p>
        </div>
      )}
      {matchupsView === 'bvp' && !matchupsLoading && !matchupsData && (
        <p className="text-muted text-center py-4">No matchup data available.</p>
      )}
      {(matchupsView === 'pitches' || matchupsView === 'last' || matchupsView === 'all' || matchupsView === 'k') && pitchesLoading && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-warning" role="status" />
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>Loading data...</p>
        </div>
      )}
      {(matchupsView === 'pitches' || matchupsView === 'last' || matchupsView === 'all' || matchupsView === 'k') && !pitchesLoading && !pitchesData && (
        <p className="text-muted text-center py-4">No data available.</p>
      )}
    </>
  );

  // Render dinger report — two sections: Bets and Top 25
  const renderDingerReport = (mobile) => {
    if (dingerLoading) {
      return <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-warning" role="status" /></div>;
    }
    if (!dingerData || dingerData.length === 0) {
      return <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d' }}>No dinger data available</div>;
    }

    const fs = mobile ? '0.7rem' : '0.8rem';
    const hdrStyle = { padding: '6px 4px', textAlign: 'center', color: '#adb5bd', fontSize: mobile ? '0.6rem' : '0.7rem', fontWeight: 600 };
    const cellStyle = { padding: '5px 4px', textAlign: 'center', color: '#dee2e6', fontSize: fs };
    const edgeColor = (e) => e > 0 ? '#28a745' : e < -0.05 ? '#dc3545' : '#dee2e6';
    const wxDisplay = (d) => d.weatherImpact != null ? `${d.weatherImpact > 0 ? '+' : ''}${Math.round(d.weatherImpact * 100)}%` : '--';

    // In 'matchup' mode filter to selected game, in 'top' mode show all games
    const gamePk = visibleGame?.gameIDmlb ? parseInt(visibleGame.gameIDmlb) : null;
    const filteredScores = dingerMode === 'matchup' && gamePk ? dingerData.filter(d => d.gamePk === gamePk) : dingerData;
    // Deduplicate: keep one row per batter+game (same batter can appear multiple times when multiple books share best odds)
    const seen = new Set();
    const allScores = filteredScores.filter(d => {
      const key = `${d.gamePk}_${d.batterName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const bets = allScores.filter(d => d.isBet).sort((a, b) => (b.edge || 0) - (a.edge || 0));
    const top25 = dingerMode === 'bvp'
      ? [...allScores].sort((a, b) => (b.components.h2hHistory || 0) - (a.components.h2hHistory || 0)).slice(0, 25)
      : [...allScores].sort((a, b) => b.score - a.score).slice(0, 25);

    // Shared column headers with tooltips
    const cols = [
      { key: '#', label: '#', title: 'Rank', align: 'center' },
      { key: 'game', label: 'Game', title: 'Game Matchup', align: 'left' },
      { key: 'batter', label: 'Batter', title: 'Batter Name', align: 'left' },
      { key: 'pitcher', label: 'vs Pitcher', title: 'Opposing Pitcher', align: 'left' },
      { key: 'score', label: 'Score', title: 'Overall HR Likelihood Score', align: 'center' },
      { key: 'odds', label: 'Odds', title: 'Best Market Odds', align: 'center' },
      { key: 'book', label: 'Book', title: 'Best Sportsbook', align: 'center' },
      { key: 'edge', label: 'Edge', title: 'Edge vs Market (Model Prob - Implied Prob)', align: 'center' },
      { key: 'kelly', label: 'K/4', title: 'Quarter-Kelly Suggested Stake', align: 'center' },
      { key: 'model', label: 'Mdl%', title: 'Model HR Probability', align: 'center' },
      { key: 'wx', label: 'wxHR', title: 'Weather Impact on HR Probability', align: 'center' },
      { key: 'bpow', label: 'BPow', title: 'Batter Power Score', align: 'center' },
      { key: 'pvul', label: 'PVul', title: 'Pitcher Vulnerability Score', align: 'center' },
      { key: 'bvul', label: 'BVul', title: 'Bullpen Vulnerability Score', align: 'center' },
      { key: 'rept', label: 'Rept', title: 'Repertoire Matchup Score', align: 'center' },
      { key: 'pltn', label: 'Pltn', title: 'Platoon Advantage Score', align: 'center' },
      { key: 'h2h', label: 'H2H', title: 'Head-to-Head History Score', align: 'center' },
      { key: 'vnue', label: 'Vnue', title: 'Venue / Park Factor Score', align: 'center' },
    ];

    const renderHeader = () => (
      <tr style={{ borderBottom: '1px solid #333', backgroundColor: '#212529' }}>
        {cols.map(c => (
          <th key={c.key} title={c.title} style={{ ...hdrStyle, textAlign: c.align, paddingLeft: c.align === 'left' ? '8px' : undefined, cursor: 'help' }}>{c.label}</th>
        ))}
      </tr>
    );

    const renderRow = (d, i, isBetSection) => {
      const scoreColor = d.score >= 65 ? '#28a745' : d.score >= 55 ? '#ffc107' : '#dee2e6';
      const bgColor = isBetSection
        ? (i % 2 === 0 ? 'rgba(40,167,69,0.06)' : 'rgba(40,167,69,0.03)')
        : (d.isBet ? 'rgba(40,167,69,0.08)' : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'));
      return (
        <tr key={i} style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '38px', backgroundColor: bgColor }}>
          <td style={{ ...cellStyle, color: '#6c757d' }}>{i + 1}</td>
          <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: '8px', color: '#adb5bd' }}>{d.matchup}</td>
          <td style={{ ...cellStyle, textAlign: 'left', color: '#fff', fontWeight: 500, cursor: onPlayerClick ? 'pointer' : 'default', textDecoration: onPlayerClick ? 'underline' : 'none', textDecorationColor: '#4b5563' }} onClick={() => {
            if (!onPlayerClick) return;
            // Look up player ID from odds data
            const gamePlayers = playersByGameIdRef.current?.[String(d.gamePk)]?.players || [];
            const match = gamePlayers.find(p => p.name === d.batterName || p.playerName === d.batterName);
            const pid = match ? String(match.playerId).replace(/^mlb_/, '') : '';
            const parts = d.matchup?.split('@') || [];
            onPlayerClick(String(d.gamePk), pid, d.batterName, null, parts[0], parts[1], d.batterTeam, null);
          }}>{d.batterName} <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>({d.batterHand})</span></td>
          <td style={{ ...cellStyle, textAlign: 'left', color: '#ffc107', cursor: onPlayerClick ? 'pointer' : 'default', textDecoration: onPlayerClick ? 'underline' : 'none', textDecorationColor: '#4b5563' }} onClick={() => {
            if (!onPlayerClick) return;
            const parts = (d.matchup?.split('@') || []).map(s => (s || '').trim());
            const pitcherTeam = d.pitcherTeam || (parts[0] === d.batterTeam ? parts[1] : parts[0]);
            const game = games.find(g => parseInt(g.gameIDmlb) === d.gamePk);
            // Look up pitcher by name in: 1) odds data, 2) game roster, 3) probable pitcher (verified)
            let pid = '';
            const target = (d.pitcherName || '').trim();
            const gamePlayers = playersByGameIdRef.current?.[String(d.gamePk)]?.players || [];
            const oddsMatch = gamePlayers.find(p => (p.name === target || p.playerName === target));
            if (oddsMatch) pid = String(oddsMatch.playerId).replace(/^mlb_/, '');
            if (!pid && game) {
              const roster = { ...(game.awayPlayers || {}), ...(game.homePlayers || {}) };
              const rosterMatch = Object.values(roster).find(p => p?.person?.fullName === target);
              if (rosterMatch?.person?.id) pid = String(rosterMatch.person.id);
            }
            if (!pid && game) {
              const isAwayPitcher = pitcherTeam === game.away;
              const probable = isAwayPitcher ? game.awayProbablePitcher : game.homeProbablePitcher;
              const probableName = probable?.fullName || probable?.name;
              if (probable?.id && (!probableName || probableName === target)) {
                pid = String(probable.id).replace(/^mlb_/, '');
              }
            }
            onPlayerClick(String(d.gamePk), pid, d.pitcherName, null, parts[0], parts[1], pitcherTeam, 'pitcher');
          }}>{d.pitcherName} <span style={{ color: '#6c757d', fontSize: '0.6rem' }}>({d.pitcherHand})</span></td>
          <td style={{ ...cellStyle, color: scoreColor, fontWeight: 700 }}>{d.score}</td>
          <td style={{ ...cellStyle }}>{d.marketOdds ? `+${d.marketOdds}` : '-'}</td>
          <td style={{ ...cellStyle }}><BookLogo book={d.marketBook} /></td>
          <td style={{ ...cellStyle, color: edgeColor(d.edge || 0), fontWeight: (d.edge || 0) > 0 ? 700 : 400 }}>{d.edge != null ? `${(d.edge * 100).toFixed(1)}%` : '-'}</td>
          <td style={{ ...cellStyle }}>{d.kellyQtr != null ? `${d.kellyQtr}u` : '-'}</td>
          <td style={{ ...cellStyle }}>{d.modelProb != null ? `${(d.modelProb * 100).toFixed(1)}%` : '-'}</td>
          <td style={{ ...cellStyle, color: d.weatherImpact > 0 ? '#28a745' : d.weatherImpact < 0 ? '#dc3545' : '#adb5bd' }}>{wxDisplay(d)}</td>
          <td style={{ ...cellStyle, color: d.components.batterPower >= 80 ? '#28a745' : '#dee2e6' }}>{d.components.batterPower}</td>
          <td style={{ ...cellStyle, color: d.components.pitcherVuln >= 70 ? '#28a745' : '#dee2e6' }}>{d.components.pitcherVuln}</td>
          <td style={{ ...cellStyle, color: (d.components.bullpenVuln || 0) >= 70 ? '#28a745' : '#dee2e6' }}>{d.components.bullpenVuln ?? '-'}</td>
          <td style={{ ...cellStyle, color: d.components.repertoireMatchup >= 80 ? '#28a745' : '#dee2e6' }}>{d.components.repertoireMatchup}</td>
          <td style={{ ...cellStyle, color: d.components.platoon >= 65 ? '#28a745' : '#dee2e6' }}>{d.components.platoon}</td>
          <td style={{ ...cellStyle, color: d.components.h2hHistory >= 65 ? '#28a745' : '#dee2e6' }}>{d.components.h2hHistory}</td>
          <td style={{ ...cellStyle, color: d.components.venue >= 60 ? '#28a745' : '#dee2e6' }}>{d.components.venue}</td>
        </tr>
      );
    };

    return (
      <div style={{ overflow: 'auto', height: '100%' }}>
        {/* Bets Section */}
        {bets.length > 0 && dingerMode !== 'bvp' && (
          <>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
              <span style={{ color: '#28a745', fontSize: '0.85rem', fontWeight: 600 }}>Best Edge</span>
              <span style={{ color: '#6c757d', fontSize: '0.7rem', marginLeft: '8px' }}>{bets.length} plays</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs, minWidth: '900px' }}>
              <thead>{renderHeader()}</thead>
              <tbody>{bets.map((d, i) => renderRow(d, i, true))}</tbody>
            </table>
            </div>
          </>
        )}

        {/* Top 25 Section */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21', borderTop: bets.length > 0 ? '1px solid #444' : 'none' }}>
          <span style={{ color: '#ffc107', fontSize: '0.85rem', fontWeight: 600 }}>{dingerMode === 'bvp' ? `Top ${Math.min(25, top25.length)} BvP Matchups` : `Top ${Math.min(25, top25.length)} HR Candidates`}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs, minWidth: '900px' }}>
          <thead>{renderHeader()}</thead>
          <tbody>{top25.map((d, i) => renderRow(d, i, false))}</tbody>
        </table>
        </div>
      </div>
    );
  };

  // Desktop panel
  if (!isMobile) return (
  <div className="dark-panel" style={{
    position: 'fixed',
    top: `${marketBannerBottom}px`,
    left: '5px',
    right: showBetslipPanel ? '410px' : '5px',
    bottom: 0,
    backgroundColor: '#212529',
    zIndex: 999,
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'right 0.2s ease'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span
          onClick={() => { if (matchupsView === 'dinger') setMatchupsView('bvp'); }}
          style={{ color: matchupsView === 'dinger' ? '#6c757d' : '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'color 0.15s' }}
        >MATCHUPS</span>
        <span style={{ color: '#555', fontSize: '0.8rem' }}>|</span>
        <span
          onClick={() => setMatchupsView('dinger')}
          style={{ color: matchupsView === 'dinger' ? '#ffc107' : '#6c757d', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'color 0.15s' }}
        >DINGER REPORT</span>
        {matchupsView !== 'dinger' && games.length > 0 && (
          <select
            value={visibleGameId || ''}
            onChange={(e) => setOverrideGameId(e.target.value)}
            style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8rem', cursor: 'pointer', maxWidth: '200px' }}
          >
            {games.map(g => (
              <option key={g.gameIDmlb} value={g.gameIDmlb}>{g.away} @ {g.home} — {g.status === 'Final' || g.status === 'Game Over' || g.status === 'Completed' ? 'Final' : g.gameTime ? (() => { const [h, m] = g.gameTime.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'p' : 'a'}`; })() : ''}</option>
            ))}
          </select>
        )}
      </div>
      <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
    </div>
    {/* View toggle */}
    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #333', flexShrink: 0, gap: '6px', flexWrap: 'wrap' }}>
      {matchupsView === 'dinger' ? (
        <>
          <span style={{ color: '#6c757d', fontSize: '0.65rem', fontStyle: 'italic', marginRight: '4px' }}>Beta</span>
          {['top', 'bvp', 'matchup'].map(mode => (
            <button
              key={mode}
              onClick={() => setDingerMode(mode)}
              style={{
                padding: '3px 12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: dingerMode === mode ? '#ffc107' : '#343a40',
                color: dingerMode === mode ? '#000' : '#adb5bd',
                cursor: 'pointer'
              }}
            >
              {mode === 'top' ? 'Top' : mode === 'bvp' ? 'BvP' : 'By Matchup'}
            </button>
          ))}
          {dingerMode === 'matchup' && games.length > 0 && (
            <select
              value={visibleGameId || ''}
              onChange={(e) => setOverrideGameId(e.target.value)}
              style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8rem', cursor: 'pointer', maxWidth: '200px', marginLeft: '4px' }}
            >
              {games.map(g => (
                <option key={g.gameIDmlb} value={g.gameIDmlb}>{g.away} @ {g.home} — {g.status === 'Final' || g.status === 'Game Over' || g.status === 'Completed' ? 'Final' : g.gameTime ? (() => { const [h, m] = g.gameTime.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'p' : 'a'}`; })() : ''}</option>
              ))}
            </select>
          )}
        </>
      ) : (
        <>
          {['bvp', 'pitches', 'last', 'all'].map(view => (
            <button
              key={view}
              onClick={() => setMatchupsView(view)}
              style={{
                padding: '3px 12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: matchupsView === view ? '#ffc107' : '#343a40',
                color: matchupsView === view ? '#000' : '#adb5bd',
                cursor: 'pointer'
              }}
            >
              {view === 'bvp' ? 'BvP' : view === 'pitches' ? 'Pitches' : view === 'last' ? 'Last' : view === 'all' ? 'All' : 'K'}
            </button>
          ))}
        </>
      )}
      {matchupsView === 'bvp' && (
        // On mobile, flexBasis 100% pushes the whole year-toggle row onto
        // its own line so the buttons stay together instead of wrapping
        // arbitrarily inside the view selector row.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['all', '2026', '2025', '2024', '2023'].map(yr => (
            <button
              key={yr}
              onClick={() => setBvpYear(yr)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: bvpYear === yr ? '#6c757d' : '#2a2d31',
                color: bvpYear === yr ? '#fff' : '#6c757d',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {yr === 'all' ? 'All*' : yr}
            </button>
          ))}
          <span style={{ color: '#555', fontSize: '0.6rem', marginLeft: '4px', flexShrink: 0 }}>* Since 2023</span>
        </div>
      )}
      {matchupsView === 'pitches' && (
        // Same pattern as BvP — on mobile, flexBasis 100% drops the whole
        // Pitches sub-menu onto its own row inside the wrap-enabled toolbar.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['summary', 'detail'].map(sub => (
            <button
              key={sub}
              onClick={() => { setPitchesSubMode(sub === 'detail' ? 'all' : sub); setPitchIdx0(0); setPitchIdx1(0); }}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: (sub === 'summary' ? pitchesSubMode === 'summary' : pitchesSubMode !== 'summary') ? '#6c757d' : '#2a2d31',
                color: (sub === 'summary' ? pitchesSubMode === 'summary' : pitchesSubMode !== 'summary') ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sub === 'summary' ? 'Summary' : 'Detail'}
            </button>
          ))}
          {isMobile ? (
            <>
              <select
                value={pitchesSplitMode}
                onChange={(e) => { setPitchesSplitMode(e.target.value); setPitchIdx0(0); setPitchIdx1(0); setSplitPitchIdx({}); }}
                style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginLeft: '4px', flexShrink: 0 }}
              >
                <option value="all">All</option>
                <option value="splits">Splits</option>
              </select>
              {pitchesSubMode === 'summary' && (
                <select
                  value={pitchesMetricMode}
                  onChange={(e) => setPitchesMetricMode(e.target.value)}
                  style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                >
                  <option value="traditional">Trad</option>
                  <option value="advanced">Adv</option>
                </select>
              )}
              <select
                value={pitchesYear}
                onChange={(e) => setPitchesYear(e.target.value)}
                style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
              >
                {['2026', '2025', '2024', '2023'].map(yr => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
              {['all', 'splits'].map(sub => (
                <button
                  key={sub}
                  onClick={() => { setPitchesSplitMode(sub); setPitchIdx0(0); setPitchIdx1(0); setSplitPitchIdx({}); }}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: pitchesSplitMode === sub ? '#6c757d' : '#2a2d31',
                    color: pitchesSplitMode === sub ? '#fff' : '#6c757d',
                    cursor: 'pointer'
                  }}
                >
                  {sub === 'all' ? 'All' : 'Splits'}
                </button>
              ))}
            </>
          )}
          {!isMobile && (
            <>
              {pitchesSubMode === 'summary' && (
                <>
                  <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
                  {['traditional', 'advanced'].map(mm => (
                    <button
                      key={mm}
                      onClick={() => setPitchesMetricMode(mm)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: pitchesMetricMode === mm ? '#6c757d' : '#2a2d31',
                        color: pitchesMetricMode === mm ? '#fff' : '#6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {mm === 'advanced' ? 'Adv' : 'Trad'}
                    </button>
                  ))}
                </>
              )}
              <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
              {['2026', '2025', '2024', '2023'].map(yr => (
                <button
                  key={yr}
                  onClick={() => setPitchesYear(yr)}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: pitchesYear === yr ? '#6c757d' : '#2a2d31',
                    color: pitchesYear === yr ? '#fff' : '#6c757d',
                    cursor: 'pointer'
                  }}
                >
                  {yr}
                </button>
              ))}
            </>
          )}
        </div>
      )}
      {matchupsView === 'last' && (
        // On mobile, flexBasis 100% drops the entire Last sub-menu onto its
        // own row so Summary/Detail + period + split selectors stay together.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['summary', 'detail'].map(sub => (
            <button
              key={sub}
              onClick={() => setLastSubMode(sub)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: lastSubMode === sub ? '#6c757d' : '#2a2d31',
                color: lastSubMode === sub ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sub === 'summary' ? 'Summary' : 'Detail'}
            </button>
          ))}
          {lastSubMode !== 'summary' && (
            <>
              {isMobile ? (
                <select
                  value={lastPeriod}
                  onChange={(e) => setLastPeriod(e.target.value)}
                  style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginLeft: '4px' }}
                >
                  <option value="last7">Last 7</option>
                  <option value="last14">Last 14</option>
                  <option value="last30">Last 30</option>
                  <option value="overall">Season</option>
                </select>
              ) : (
                <>
                  <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
                  {['last7', 'last14', 'last30', 'overall'].map(p => (
                    <button
                      key={p}
                      onClick={() => setLastPeriod(p)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: lastPeriod === p ? '#6c757d' : '#2a2d31',
                        color: lastPeriod === p ? '#fff' : '#6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {p === 'overall' ? 'Season' : p === 'last7' ? 'Last 7' : p === 'last14' ? 'Last 14' : 'Last 30'}
                    </button>
                  ))}
                </>
              )}
            </>
          )}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['overall', 'vsLHP', 'vsRHP'].map(sp => (
            <button
              key={sp}
              onClick={() => setLastSplit(sp)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: lastSplit === sp ? '#6c757d' : '#2a2d31',
                color: lastSplit === sp ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sp === 'overall' ? 'Overall' : sp === 'vsLHP' ? 'vs LHP' : 'vs RHP'}
            </button>
          ))}
        </div>
      )}
      {matchupsView === 'k' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['smooth', 'sections'].map(m => (
            <button
              key={m}
              onClick={() => setKHeatmapMode(m)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kHeatmapMode === m ? '#6c757d' : '#2a2d31',
                color: kHeatmapMode === m ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {m === 'smooth' ? 'Smooth' : 'Sections'}
            </button>
          ))}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['individual', 'composite'].map(m => (
            <button
              key={m}
              onClick={() => setKLayoutMode(m)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kLayoutMode === m ? '#6c757d' : '#2a2d31',
                color: kLayoutMode === m ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {m === 'individual' ? 'Individual' : 'Composite'}
            </button>
          ))}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {[
            { v: 'swings', label: 'Whf/Swg', title: 'Whiffs per swing (miss rate on swings)' },
            { v: 'pitches', label: 'Whf/Pch', title: 'Whiffs per pitch (SwStr%)' },
          ].map(({ v, label, title }) => (
            <button
              key={v}
              onClick={() => setKWhiffDenom(v)}
              title={title}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kWhiffDenom === v ? '#6c757d' : '#2a2d31',
                color: kWhiffDenom === v ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
    <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
      {matchupsContent}
      {matchupsView === 'bvp' && !matchupsLoading && matchupsData && (
        matchupAtBatBatter ? (
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #333' }}>
              {renderMatchupsTable(homeTeam, awayTeam, 0, false)}
              {renderMatchupsTable(awayTeam, homeTeam, 1, false)}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {renderAtBatDetail()}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0', height: '100%' }}>
            {renderMatchupsTable(homeTeam, awayTeam, 0, false)}
            {renderMatchupsTable(awayTeam, homeTeam, 1, false)}
          </div>
        )
      )}
      {matchupsView === 'pitches' && !pitchesLoading && pitchesData && matchupsData && (
        pitchesSubMode === 'summary' ? (
          renderPitchSummary(false)
        ) : matchupAtBatBatter ? (
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #333' }}>
              {renderPitchesTable(homeTeam, awayTeam, 0, false)}
              {renderPitchesTable(awayTeam, homeTeam, 1, false)}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {renderAtBatDetail()}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0', height: '100%' }}>
            {renderPitchesTable(homeTeam, awayTeam, 0, false)}
            {renderPitchesTable(awayTeam, homeTeam, 1, false)}
          </div>
        )
      )}
      {matchupsView === 'last' && !pitchesLoading && pitchesData && matchupsData && (
        lastSubMode === 'summary' ? (
          renderLastSummary(false)
        ) : (
          <div style={{ display: 'flex', gap: '0', height: '100%' }}>
            {renderLastTable(homeTeam, awayTeam, 0, false)}
            {renderLastTable(awayTeam, homeTeam, 1, false)}
          </div>
        )
      )}
      {matchupsView === 'all' && !pitchesLoading && pitchesData && matchupsData && renderAllView(false)}
      {matchupsView === 'k' && !pitchesLoading && pitchesData && matchupsData && renderKView(false)}
      {matchupsView === 'dinger' && renderDingerReport(false)}
    </div>
    {ballFlightPlay && (
      <div style={{ position: 'fixed', top: `${marketBannerBottom}px`, left: '5px', right: '50%', bottom: 0, backgroundColor: 'rgba(0,0,0,0.98)', zIndex: 9999, display: 'flex', flexDirection: 'column', borderRadius: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', flexShrink: 0, borderBottom: '1px solid #333', backgroundColor: '#1a1d21', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
          <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>Ball Flight</span>
          <button onClick={() => setBallFlightPlay(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}>×</button>
        </div>
        <iframe src={`/ball-flight/embed?playId=${ballFlightPlay.playId}&season=${ballFlightPlay.season}`} style={{ flex: 1, border: 'none' }} title="Ball Flight" />
      </div>
    )}
  </div>
  );

  // Mobile overlay
  return (
  <div style={{
    position: 'fixed',
    top: `${marketBannerBottom}px`,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #333' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span
          onClick={() => { if (matchupsView === 'dinger') setMatchupsView('bvp'); }}
          style={{ color: matchupsView === 'dinger' ? '#6c757d' : '#ffc107', fontFamily: "'Oswald', sans-serif", fontSize: '1.1em', fontWeight: '600', cursor: 'pointer', transition: 'color 0.15s' }}
        >MATCHUPS</span>
        <span style={{ color: '#555', fontSize: '0.8rem' }}>|</span>
        <span
          onClick={() => setMatchupsView('dinger')}
          style={{ color: matchupsView === 'dinger' ? '#ffc107' : '#6c757d', fontFamily: "'Oswald', sans-serif", fontSize: '1.1em', fontWeight: '600', cursor: 'pointer', transition: 'color 0.15s' }}
        >DINGER REPORT</span>
        {matchupsView !== 'dinger' && games.length > 0 && (
          <select
            value={visibleGameId || ''}
            onChange={(e) => setOverrideGameId(e.target.value)}
            style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '4px 6px', fontSize: '0.8rem', cursor: 'pointer', maxWidth: '200px' }}
          >
            {games.map(g => (
              <option key={g.gameIDmlb} value={g.gameIDmlb}>{g.away} @ {g.home} — {g.status === 'Final' || g.status === 'Game Over' || g.status === 'Completed' ? 'Final' : g.gameTime ? (() => { const [h, m] = g.gameTime.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'p' : 'a'}`; })() : ''}</option>
            ))}
          </select>
        )}
      </div>
      <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px' }}>×</button>
    </div>
    {/* View toggle */}
    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #333', flexShrink: 0, gap: '6px', flexWrap: 'wrap' }}>
      {matchupsView === 'dinger' ? (
        <>
          <span style={{ color: '#6c757d', fontSize: '0.65rem', fontStyle: 'italic', marginRight: '4px' }}>Beta</span>
          {['top', 'bvp', 'matchup'].map(mode => (
            <button
              key={mode}
              onClick={() => setDingerMode(mode)}
              style={{
                padding: '3px 12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: dingerMode === mode ? '#ffc107' : '#343a40',
                color: dingerMode === mode ? '#000' : '#adb5bd',
                cursor: 'pointer'
              }}
            >
              {mode === 'top' ? 'Top' : mode === 'bvp' ? 'BvP' : 'By Matchup'}
            </button>
          ))}
          {dingerMode === 'matchup' && games.length > 0 && (
            <select
              value={visibleGameId || ''}
              onChange={(e) => setOverrideGameId(e.target.value)}
              style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8rem', cursor: 'pointer', maxWidth: '200px', marginLeft: '4px' }}
            >
              {games.map(g => (
                <option key={g.gameIDmlb} value={g.gameIDmlb}>{g.away} @ {g.home} — {g.status === 'Final' || g.status === 'Game Over' || g.status === 'Completed' ? 'Final' : g.gameTime ? (() => { const [h, m] = g.gameTime.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'p' : 'a'}`; })() : ''}</option>
              ))}
            </select>
          )}
        </>
      ) : (
        <>
          {['bvp', 'pitches', 'last', 'all'].map(view => (
            <button
              key={view}
              onClick={() => setMatchupsView(view)}
              style={{
                padding: '3px 12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: matchupsView === view ? '#ffc107' : '#343a40',
                color: matchupsView === view ? '#000' : '#adb5bd',
                cursor: 'pointer'
              }}
            >
              {view === 'bvp' ? 'BvP' : view === 'pitches' ? 'Pitches' : view === 'last' ? 'Last' : view === 'all' ? 'All' : 'K'}
            </button>
          ))}
        </>
      )}
      {matchupsView === 'bvp' && (
        // On mobile, flexBasis 100% pushes the whole year-toggle row onto
        // its own line so the buttons stay together instead of wrapping
        // arbitrarily inside the view selector row.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['all', '2026', '2025', '2024', '2023'].map(yr => (
            <button
              key={yr}
              onClick={() => setBvpYear(yr)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: bvpYear === yr ? '#6c757d' : '#2a2d31',
                color: bvpYear === yr ? '#fff' : '#6c757d',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {yr === 'all' ? 'All*' : yr}
            </button>
          ))}
          <span style={{ color: '#555', fontSize: '0.6rem', marginLeft: '4px', flexShrink: 0 }}>* Since 2023</span>
        </div>
      )}
      {matchupsView === 'pitches' && (
        // Same pattern as BvP — on mobile, flexBasis 100% drops the whole
        // Pitches sub-menu onto its own row inside the wrap-enabled toolbar.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['summary', 'detail'].map(sub => (
            <button
              key={sub}
              onClick={() => { setPitchesSubMode(sub === 'detail' ? 'all' : sub); setPitchIdx0(0); setPitchIdx1(0); }}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: (sub === 'summary' ? pitchesSubMode === 'summary' : pitchesSubMode !== 'summary') ? '#6c757d' : '#2a2d31',
                color: (sub === 'summary' ? pitchesSubMode === 'summary' : pitchesSubMode !== 'summary') ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sub === 'summary' ? 'Summary' : 'Detail'}
            </button>
          ))}
          {isMobile ? (
            <>
              <select
                value={pitchesSplitMode}
                onChange={(e) => { setPitchesSplitMode(e.target.value); setPitchIdx0(0); setPitchIdx1(0); setSplitPitchIdx({}); }}
                style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginLeft: '4px', flexShrink: 0 }}
              >
                <option value="all">All</option>
                <option value="splits">Splits</option>
              </select>
              {pitchesSubMode === 'summary' && (
                <select
                  value={pitchesMetricMode}
                  onChange={(e) => setPitchesMetricMode(e.target.value)}
                  style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                >
                  <option value="traditional">Trad</option>
                  <option value="advanced">Adv</option>
                </select>
              )}
              <select
                value={pitchesYear}
                onChange={(e) => setPitchesYear(e.target.value)}
                style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
              >
                {['2026', '2025', '2024', '2023'].map(yr => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
              {['all', 'splits'].map(sub => (
                <button
                  key={sub}
                  onClick={() => { setPitchesSplitMode(sub); setPitchIdx0(0); setPitchIdx1(0); setSplitPitchIdx({}); }}
                  style={{
                    padding: '3px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: pitchesSplitMode === sub ? '#6c757d' : '#2a2d31',
                    color: pitchesSplitMode === sub ? '#fff' : '#6c757d',
                    cursor: 'pointer'
                  }}
                >
                  {sub === 'all' ? 'All' : 'Splits'}
                </button>
              ))}
            </>
          )}
        </div>
      )}
      {matchupsView === 'last' && (
        // On mobile, flexBasis 100% drops the entire Last sub-menu onto its
        // own row so Summary/Detail + period + split selectors stay together.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['summary', 'detail'].map(sub => (
            <button
              key={sub}
              onClick={() => setLastSubMode(sub)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: lastSubMode === sub ? '#6c757d' : '#2a2d31',
                color: lastSubMode === sub ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sub === 'summary' ? 'Summary' : 'Detail'}
            </button>
          ))}
          {lastSubMode !== 'summary' && (
            <>
              {isMobile ? (
                <select
                  value={lastPeriod}
                  onChange={(e) => setLastPeriod(e.target.value)}
                  style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '4px', padding: '3px 4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginLeft: '4px' }}
                >
                  <option value="last7">Last 7</option>
                  <option value="last14">Last 14</option>
                  <option value="last30">Last 30</option>
                  <option value="overall">Season</option>
                </select>
              ) : (
                <>
                  <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />
                  {['last7', 'last14', 'last30', 'overall'].map(p => (
                    <button
                      key={p}
                      onClick={() => setLastPeriod(p)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: lastPeriod === p ? '#6c757d' : '#2a2d31',
                        color: lastPeriod === p ? '#fff' : '#6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {p === 'overall' ? 'Season' : p === 'last7' ? 'Last 7' : p === 'last14' ? 'Last 14' : 'Last 30'}
                    </button>
                  ))}
                </>
              )}
            </>
          )}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['overall', 'vsLHP', 'vsRHP'].map(sp => (
            <button
              key={sp}
              onClick={() => setLastSplit(sp)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: lastSplit === sp ? '#6c757d' : '#2a2d31',
                color: lastSplit === sp ? '#fff' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              {sp === 'overall' ? 'Overall' : sp === 'vsLHP' ? 'vs LHP' : 'vs RHP'}
            </button>
          ))}
        </div>
      )}
      {matchupsView === 'k' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          flexWrap: 'nowrap',
          ...(isMobile ? { flexBasis: '100%', marginTop: '4px' } : {}),
        }}>
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['smooth', 'sections'].map(m => (
            <button
              key={m}
              onClick={() => setKHeatmapMode(m)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kHeatmapMode === m ? '#6c757d' : '#2a2d31',
                color: kHeatmapMode === m ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {m === 'smooth' ? 'Smooth' : 'Sections'}
            </button>
          ))}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {['individual', 'composite'].map(m => (
            <button
              key={m}
              onClick={() => setKLayoutMode(m)}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kLayoutMode === m ? '#6c757d' : '#2a2d31',
                color: kLayoutMode === m ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {m === 'individual' ? 'Individual' : 'Composite'}
            </button>
          ))}
          {!isMobile && <div style={{ width: '1px', height: '18px', backgroundColor: '#555', margin: '0 4px' }} />}
          {[
            { v: 'swings', label: 'Whf/Swg', title: 'Whiffs per swing (miss rate on swings)' },
            { v: 'pitches', label: 'Whf/Pch', title: 'Whiffs per pitch (SwStr%)' },
          ].map(({ v, label, title }) => (
            <button
              key={v}
              onClick={() => setKWhiffDenom(v)}
              title={title}
              style={{
                padding: '3px 10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: '4px',
                border: 'none',
                backgroundColor: kWhiffDenom === v ? '#6c757d' : '#2a2d31',
                color: kWhiffDenom === v ? '#fff' : '#6c757d',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
    <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
      {matchupsContent}
      {matchupsView === 'bvp' && !matchupsLoading && matchupsData && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {renderMatchupsTable(homeTeam, awayTeam, 0, true)}
          {renderMatchupsTable(awayTeam, homeTeam, 1, true)}
        </div>
      )}
      {matchupsView === 'pitches' && !pitchesLoading && pitchesData && matchupsData && (
        pitchesSubMode === 'summary' ? (
          renderPitchSummary(true)
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {renderPitchesTable(homeTeam, awayTeam, 0, true)}
            {renderPitchesTable(awayTeam, homeTeam, 1, true)}
          </div>
        )
      )}
      {matchupsView === 'last' && !pitchesLoading && pitchesData && matchupsData && (
        lastSubMode === 'summary' ? (
          renderLastSummary(true)
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {renderLastTable(homeTeam, awayTeam, 0, true)}
            {renderLastTable(awayTeam, homeTeam, 1, true)}
          </div>
        )
      )}
      {matchupsView === 'all' && !pitchesLoading && pitchesData && matchupsData && renderAllView(true)}
      {matchupsView === 'k' && !pitchesLoading && pitchesData && matchupsData && renderKView(true)}
      {matchupsView === 'dinger' && renderDingerReport(true)}
    </div>
    {/* At-bat detail overlay (mobile) */}
    {matchupAtBatBatter && (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.97)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}>
        {renderAtBatDetail()}
      </div>
    )}
    {ballFlightPlay && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.98)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', flexShrink: 0, borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
          <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>Ball Flight</span>
          <button onClick={() => setBallFlightPlay(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}>×</button>
        </div>
        <iframe src={`/ball-flight/embed?playId=${ballFlightPlay.playId}&season=${ballFlightPlay.season}`} style={{ flex: 1, border: 'none' }} title="Ball Flight" />
      </div>
    )}
  </div>
  );
};


export default MatchupsPanel;
